import { SEGMENTOS_EXAMEN } from './segmentosExamen';
// Examen físico de ingreso — dominio puro de MiPAE en turno.
//
// El dolor de jefatura que motiva este módulo: el examen físico temprano SÍ se
// hace, pero no queda registro explícito de que se hizo (ni de a qué hora).
// Aquí queda: un recorrido céfalo-caudal por segmento, con hallazgos de
// catálogo cerrado + nota libre, sellado en el momento en que aterriza el
// primer dato. Dos tiempos con roles distintos (decisión 2026-07-11): `hora`
// es la hora del EXAMEN y el autor puede corregirla (el examen real pudo ser
// antes de alcanzar a registrarlo); `ts` es el sello del SISTEMA (cuándo se
// registró) y NO se edita — esa inmutabilidad es la evidencia de registro
// temprano que jefatura audita.
//
// Valor médico-legal concreto: una LPP en zona sacra documentada al recibir al
// paciente es la diferencia entre "venía de antes" y "se produjo en tu turno".
//
// Diseño (decisiones de producto 2026-07-07, con el fundador):
// - Es la FOTO al recibir; la evolución por sistema es la película del turno.
//   Sección hermana por paciente, nunca mezclada con la evolución.
// - La velocidad es el producto: "Sin hallazgos" a 1 toque por segmento
//   (evaluado y normal ≠ no evaluado — mismo oro clínico de la evolución);
//   un examen normal completo se registra en menos de un minuto.
// - Chips + nota en el MVP; campos estructurados finos solo si el uso real
//   los pide (v2).
// - No re-registra invasivos ni signos vitales: eso vive en la evolución.
//   Los hallazgos que SÍ le aportan a la evolución viajan por un puente
//   determinista e idempotente (solo catálogo cerrado, nunca la nota libre —
//   misma garantía de PII que las tools de Benito).

import { SISTEMAS as SISTEMAS_EVOLUCION, generizarFemenino } from './evolucionSistema';
import { detectPii, detectPiiDuro } from './piiDetect';
import { ESCALAS_DEF, scoreEscala, escalaConValores, CAMPOS_POR_SISTEMA, joinProsaLista, cumpleDependencia, valorNormalDeCampo, dosisConUnidad } from './escalasEvolucion';
import {
  lineasPiel, pielTieneContenido, resumenPielPrevio, segmentosConLesion, ZONAS_PRESION,
  conRevision, nuevaRevision, pielIndemneAlIngreso, anclaLesion,
} from './pielLpp';

// ── Catálogo céfalo-caudal ────────────────────────────────────────────────────
// Orden como se examina de verdad: de la cabeza a los pies y el giro
// (espalda + zona sacra) al final. Los hallazgos son ANORMALIDADES de catálogo
// cerrado — lo normal se declara con el flag "sin hallazgos" del segmento.
// `evolucion` (opcional, por hallazgo) declara a qué sistema de la evolución
// aporta ese hallazgo — es la tabla del puente, explícita, sin adivinar.
// `red` (opcional) marca las banderas rojas de seguridad con la letra ABCDE que
// sugieren (auditoría 2026-07-09, D-9): la UI muestra una pista — apoya el
// razonamiento, NUNCA diagnostica ni indica conducta farmacológica.
// Etiquetas decididas por el fundador (D-3, propuesta clínica completa):
// Palidez→hemodinamia (perfusión/anemia) · Ictericia→gastro (patrón hepático) ·
// Mucosas resecas→gastro (hidratación/nutricional) · Edema facial→hemodinamia ·
// Rigidez cervical→neuro · Movilidad disminuida→neuro. Adenopatías, aumento de
// volumen cervical, deformidad de columna, dolor de espalda, edema genital y
// secreción/sangrado quedan como hallazgo de foto (multicausales — se leen en
// el texto del examen pero no le adivinan un sistema a la película).
export { SEGMENTOS_EXAMEN } from './segmentosExamen';

// Impresión general de apertura (1 toque): lo primero que la enfermera
// constata al entrar a la unidad. Da encabezado clínico al texto.
export const IMPRESIONES_GENERALES = ['Buen estado general', 'Regular estado general', 'Mal estado general'];
// Semáforo de la impresión general (dictado 2026-07-18): "clasificarlos" con
// color clínico — SIEMPRE vocabulario clinical-* (estable/attention/critical),
// nunca color crudo. La UI mapea el tono al chip seleccionado.
export const IMPRESION_TONO = {
  'Buen estado general': 'stable',
  'Regular estado general': 'attention',
  'Mal estado general': 'critical',
};

// Grupos de hallazgos por segmento (dictado 2026-07-18): la UI muestra los
// chips agrupados con su etiqueta, en este orden — el desorden de una lista
// plana de 8 chips era el reclamo. Los segmentos sin entrada aquí siguen con
// su lista plana de siempre.
export const GRUPOS_SEGMENTO = {
  cabeza: [
    { id: 'craneo', label: 'Cráneo y cara' },
    { id: 'pupilas', label: 'Pupilas', campos: ['pupilas_reactividad'] },
    { id: 'mucosas', label: 'Mucosas y coloración' },
    { id: 'boca', label: 'Boca' },
    { id: 'tubo', label: 'Tubo orotraqueal', campos: ['tot_cm', 'tot_cuff'] },
    { id: 'otros', label: 'Otros hallazgos' },
  ],
  cuello: [
    { id: 'inspeccion', label: 'Inspección' },
    // Traqueostomía (dictado del fundador en turno real, 2026-08-20: "falta en
    // cuello la traqueo, más que todo el estoma, si se ve vital, si tiene
    // puntos"). Es un subsegmento de solo-campos: la fila va COLAPSADA como
    // todas (#685), así que a quien no tiene traqueostomía no le cuesta nada, y
    // quien la tiene la encuentra donde la mira. Sin `NORMAL_GRUPO` a propósito:
    // igual que "tubo", "drenajes" y "ostomías", es un grupo de PRESENCIA — no
    // hay una normalidad del cuello que firmar aquí.
    { id: 'traqueo', label: 'Traqueostomía', campos: ['tqt_estoma', 'tqt_secrecion', 'tqt_puntos', 'tqt_fijacion', 'tqt_cuff'] },
    { id: 'palpacion', label: 'Palpación' },
    { id: 'piel', label: 'Piel y cobertura' },
  ],
  torax: [
    { id: 'patron', label: 'Patrón respiratorio', campos: ['patron_resp'] },
    { id: 'musculatura', label: 'Musculatura accesoria' },
    { id: 'auscultacion', label: 'Auscultación', campos: ['auscultacion'] },
    { id: 'drenajes', label: 'Drenajes' },
    { id: 'piel', label: 'Lesiones en piel' },
    { id: 'otros', label: 'Otros hallazgos' },
  ],
  abdomen: [
    { id: 'palpacion', label: 'A la palpación', campos: ['blandura'] },
    { id: 'percusion', label: 'A la percusión', campos: ['percusion'] },
    { id: 'rha', label: 'Ruidos hidroaéreos', campos: ['rha'] },
    { id: 'ostomias', label: 'Ostomías y drenajes' },
    { id: 'piel', label: 'Piel y herida operatoria' },
    { id: 'otros', label: 'Otros hallazgos' },
  ],
  eess: [
    { id: 'motor', label: 'Movilidad, fuerza y sensibilidad', campos: ['fuerza'] },
    { id: 'perfusion', label: 'Perfusión y pulsos', campos: ['pulsos', 'llene_capilar_seg'] },
    { id: 'edema', label: 'Edema' },
    { id: 'piel', label: 'Piel' },
  ],
  eeii: [
    { id: 'motor', label: 'Movilidad, fuerza y sensibilidad', campos: ['fuerza'] },
    { id: 'perfusion', label: 'Perfusión y pulsos', campos: ['pulsos', 'llene_capilar_seg'] },
    { id: 'edema', label: 'Edema' },
    { id: 'piel', label: 'Piel' },
  ],
  genitales: [
    { id: 'dai', label: 'Dermatitis por humedad (DAI)', campos: ['dai_localizacion'] },
    { id: 'otros', label: 'Otros hallazgos' },
  ],
  espalda: [
    { id: 'columna', label: 'Columna' },
    { id: 'piel', label: 'Piel y zonas de apoyo' },
  ],
  sacra: [
    { id: 'apoyo', label: 'Puntos de apoyo' },
    { id: 'humedad', label: 'Humedad' },
    { id: 'proteccion', label: 'Protección' },
  ],
};

// Subsegmento que EDITA la piel de este segmento, si el catálogo lo declara
// (grupo id 'piel' en GRUPOS_SEGMENTO). PR K ("la visita deja de cobrar
// navegación", 2026-08-16): cuando el segmento ya tiene una lesión dibujada
// en el mapa corporal, no tiene sentido que el subsegmento de piel llegue
// COLAPSADO — abrirlo es un toque de peaje sin decisión adentro. Derivado
// 100% de GRUPOS_SEGMENTO (cero catálogo paralelo): en cabeza, sacra y
// genitales, donde la piel no vive en un grupo propio de este editor (sacra
// la confirma en su propia estación, `EstadoPielPanel`), devuelve null y
// NO se adivina un subsegmento que no existe.
export function subsegmentoDeLesion(segmentoId) {
  // `|| []` no basta: una clave del prototipo ('__proto__', 'constructor',
  // 'toString') devuelve algo truthy que no es array y revienta en .find.
  // Doctrina #642, re-validar al LEER: un id que no es del catálogo no
  // adivina ni rompe, devuelve null.
  const grupos = GRUPOS_SEGMENTO[segmentoId];
  if (!Array.isArray(grupos)) return null;
  return grupos.find((g) => g.id === 'piel')?.id || null;
}

// Fragmento basal por PARTE del segmento (dictado del fundador 2026-07-18:
// "en cada parte se pide si es normocráneo, y si no, describir"). Cada grupo
// de chips ofrece su normal afirmable; marcarlo imprime este fragmento, no
// marcarlo + elegir un chip = describir la anomalía. Solo los grupos de chips
// con un normal claro lo declaran; los grupos-campo (pupilas, auscultación…)
// ya traen su normal como opción del campo, y los grupos de solo-presencia
// (drenajes, ostomías, protección) no tienen "normal" que afirmar.
// Se despliega por segmento a medida que el fundador confirma la redacción.
export const NORMAL_GRUPO = {
  cabeza: {
    craneo: 'normocéfalo, facies simétrica, sin lesiones en cuero cabelludo ni cara',
    mucosas: 'mucosas rosadas e hidratadas, sin cianosis',
    boca: 'mucosa oral sana, sin lesiones',
  },
  cuello: {
    inspeccion: 'sin ingurgitación yugular, tráquea central, sin aumento de volumen',
    palpacion: 'móvil e indoloro, sin adenopatías',
    piel: 'piel indemne',
  },
  torax: {
    musculatura: 'sin uso de musculatura accesoria',
    drenajes: 'sin drenajes torácicos',
    piel: 'piel indemne, zonas de apoyo sanas',
  },
  abdomen: {
    ostomias: 'sin ostomías ni drenajes',
    piel: 'piel indemne, sin herida operatoria, zonas de apoyo sanas',
  },
  eess: {
    motor: 'movilidad conservada, sensibilidad indemne',
    perfusion: 'extremidades tibias, sin frialdad distal',
    edema: 'sin edema',
    piel: 'piel indemne, bien hidratada, sin lesiones',
  },
  eeii: {
    motor: 'movilidad conservada, sensibilidad indemne',
    perfusion: 'extremidades tibias, sin frialdad distal ni signos de TVP',
    edema: 'sin edema',
    piel: 'piel indemne, bien hidratada, sin lesiones',
  },
  genitales: {
    dai: 'sin dermatitis asociada a humedad',
    otros: 'sin edema genital ni secreciones',
  },
  espalda: {
    columna: 'columna alineada, indolora',
    piel: 'piel indemne, sin eritema en zonas de apoyo',
  },
  sacra: {
    apoyo: 'zonas de apoyo sin eritema ni signos de presión',
    humedad: 'sin maceración por humedad',
  },
};

// Campos del modo detallado que un grupo declara como FIJOS de su segmento
// (se muestran siempre, sin abrir "profundizar") — derivado de los grupos:
// una sola fuente de verdad para qué es examen mínimo y qué es detalle.
export const CAMPOS_FIJOS_SEGMENTO = Object.fromEntries(
  Object.entries(GRUPOS_SEGMENTO).map(([id, gs]) => [id, gs.flatMap((g) => g.campos || [])]),
);
export const POSICIONES = ['Decúbito supino', 'Semisentado', 'Decúbito lateral', 'Prono', 'Sentado', 'Deambulando'];
// Estado de conciencia al recibir: el baseline neurológico de la foto, a 1
// toque. "Vigil y conectado/a" es lo normal; el resto son grados de deterioro.
// No re-registra Glasgow ni RASS (eso es la evolución/escalas) — es la lectura
// rápida médico-legal de "cómo llegó" (un sopor documentado al ingreso vale).
export const CONCIENCIA = ['Vigil y conectado', 'Somnoliento', 'Sopor', 'Agitación psicomotora', 'No responde'];
// Grados de conciencia que son bandera roja al recibir (sugieren la D del
// ABCDE): un sopor o un no-responde de ENTRADA merece pista de seguridad —
// misma regla que los hallazgos `red` del catálogo.
export const CONCIENCIA_RED = { Sopor: 'D', 'No responde': 'D' };
// "Conciencia comprometida" de esta pantalla, derivada de la MISMA bandera roja
// (cero catálogo paralelo). Se lee como lista y no como lookup para que un
// valor fuera de catálogo —jsonb adulterado— no encienda nada por herencia de
// Object.prototype.
const CONCIENCIA_COMPROMETIDA = Object.keys(CONCIENCIA_RED);

// ── Signos de contexto de la foto (modo detallado, opcional) ─────────────────
// Al recibir se toman signos: capturarlos aquí sella el baseline sin re-tipear
// (la evolución sigue siendo la dueña de la tendencia del turno). Todos
// numéricos y opcionales — el examen normal en <1 min NO exige signos.
// PAM/SpO₂-FiO₂ no se piden: se derivan con el motor de escalas (scorePam).
export const SIGNOS_EXAMEN = [
  { k: 'fc',        label: 'FC',          unidad: 'lpm' },
  { k: 'pas',       label: 'PAS',         unidad: 'mmHg' },
  { k: 'pad',       label: 'PAD',         unidad: 'mmHg' },
  { k: 'fr',        label: 'FR',          unidad: 'rpm' },
  { k: 'spo2',      label: 'SpO₂',        unidad: '%' },
  { k: 'fio2',      label: 'FiO₂',        unidad: '%' },
  { k: 'temp',      label: 'T°',          unidad: '°C' },
  { k: 'hgt',       label: 'HGT',         unidad: 'mg/dL' },
  { k: 'dolor_eva', label: 'Dolor (EVA)', unidad: '/10' },
];

// ── Evaluación primaria ABCDE (capa opcional para crítico) ───────────────────
// No reemplaza el recorrido por segmento: es la banda de priorización de
// "lo que mata primero" (UCI/urgencias). El prompt de diagnósticos del PAE YA
// prioriza por ABCDE — esta capa lo alimenta desde la captura. Cada letra:
// 'ok' | 'alterado' | null (null = no evaluada; el silencio nunca inventa).
export const ABCDE_EXAMEN = [
  { k: 'a', letra: 'A', label: 'Vía aérea' },
  { k: 'b', letra: 'B', label: 'Ventilación' },
  { k: 'c', letra: 'C', label: 'Circulación' },
  { k: 'd', letra: 'D', label: 'Déficit neurológico' },
  { k: 'e', letra: 'E', label: 'Exposición / piel' },
];

// ── Campos estructurados por segmento (modo detallado, opcional) ─────────────
// El acordeón "Profundizar" de cada segmento (auditoría 2026-07-09, D-1
// iteración completa): número+unidad o select de catálogo cerrado — mismos
// tipos y misma interpretación (`interpretarCampo`) que CAMPOS_POR_SISTEMA de
// la evolución, para no inventar un segundo motor. Nadie está obligado: el
// modo básico (chips + nota) sigue intacto y el detalle es por segmento.
// En sacra, `lpp_categoria`/`lpp_localizacion` convierten "LPP presente al
// ingreso" en el dato duro que Calidad audita (ancla LPP, médico-legal).
// Presión del balón, MISMOS cortes para el TOT (boca) y para la cánula de
// traqueostomía (cuello): es el mismo balón sellando la misma tráquea. Fuente
// única para que las dos no se desalineen en silencio (norma de coherencia).
const BANDAS_CUFF = [
  { hasta: 19.9, texto: 'Cuff bajo (<20): riesgo de aspiración, verificar sello.', nivel: 'moderado' },
  { hasta: 30,   texto: 'Presión de cuff adecuada (20 a 30 cmH₂O).', nivel: 'normal' },
  { hasta: 99,   texto: 'Cuff alto (>30): riesgo de isquemia traqueal.', nivel: 'moderado' },
];

export const CAMPOS_EXAMEN = {
  cabeza: [
    // Pupilas es campo FIJO del segmento (dictado 2026-07-18): se muestra
    // siempre, sin abrir "profundizar" (ver CAMPOS_FIJOS_SEGMENTO). "Perezosas"
    // se retiró del catálogo por decisión del fundador. El select de mucosas
    // murió: las mucosas son chips del grupo homónimo (catálogo del segmento).
    { k: 'pupilas_reactividad', label: 'Pupilas', tipo: 'select',
      opciones: ['Isocóricas y fotorreactivas', 'Fijas', 'Anisocóricas'],
      notas: {
        'Isocóricas y fotorreactivas': { nivel: 'normal', texto: 'Esperado.' },
        Fijas:          { nivel: 'severo', texto: 'Pupilas fijas: avisar y objetivar con Glasgow.' },
        Anisocóricas:   { nivel: 'severo', texto: 'Anisocoria nueva es bandera neurológica: avisar.' },
      } },
    { k: 'pupilas_mm', label: 'Tamaño pupilar', tipo: 'num', unidad: 'mm', bandas: [
      { hasta: 1.9, texto: 'Miosis: correlaciona con sedación/opioides.', nivel: 'moderado' },
      { hasta: 4.5, texto: 'Tamaño esperado (2–4,5 mm).', nivel: 'normal' },
      { hasta: 99,  texto: 'Midriasis: correlaciona con fármacos o compromiso neurológico.', nivel: 'moderado' },
    ] },
    // Tubo orotraqueal (dictado 2026-07-18): número en comisura y presión de
    // cuff. Vacíos = no se registran (silencio), como cualquier campo.
    { k: 'tot_cm', label: 'Tubo, cm en comisura', tipo: 'num', unidad: 'cm' },
    { k: 'tot_cuff', label: 'Cuff', tipo: 'num', unidad: 'cmH₂O', bandas: BANDAS_CUFF },
    // ── DVE (mudado desde el neuro de la evolución, pedido del fundador
    // 2026-08-10): el detalle del drenaje se constata MIRANDO el circuito en
    // la visita — cámara, columna, bolsa — así que su casa es el examen. Los
    // campos con `dependeDe` (misma semántica de la evolución, evaluada con
    // `cumpleDependencia`) solo aparecen e imprimen con "Con DVE" marcado.
    // La didáctica por opción viaja con la rama: es la parte docente.
    { k: 'dve', label: 'Drenaje ventricular externo (DVE)', tipo: 'select',
      opciones: ['Sin DVE', 'Con DVE'],
      notas: {
        'Con DVE': { nivel: 'moderado', texto: 'Cámara SIEMPRE a la altura prescrita respecto del cero; al movilizar o trasladar, clampear según indicación y reabrir al reposicionar.' },
      } },
    { k: 'dve_cero', label: 'Cero de referencia', tipo: 'select', dependeDe: { campo: 'dve', en: ['Con DVE'] },
      opciones: ['En pabellón auricular', 'En trago'],
      notas: {
        'En pabellón auricular': { nivel: 'normal', texto: 'El cero se nivela con el agujero de Monro: en la práctica se referencia al pabellón auricular o al trago.' },
        'En trago':              { nivel: 'normal', texto: 'El cero se nivela con el agujero de Monro: en la práctica se referencia al pabellón auricular o al trago.' },
      } },
    { k: 'dve_altura', label: 'Altura prescrita', tipo: 'num', unidad: 'cmH₂O', dependeDe: { campo: 'dve', en: ['Con DVE'] },
      bandas: [
        { hasta: 99, texto: 'La altura de la cámara sobre el cero ES la prescripción: más alto drena menos, más bajo drena más.', nivel: 'normal' },
      ] },
    { k: 'dve_oscilacion', label: 'Oscilación de la columna', tipo: 'select', dependeDe: { campo: 'dve', en: ['Con DVE'] },
      opciones: ['Oscilante', 'No oscilante'],
      notas: {
        'Oscilante':    { nivel: 'normal',   texto: 'La columna oscila con el pulso y la respiración: sistema permeable.' },
        'No oscilante': { nivel: 'moderado', texto: 'Sin oscilación: puede estar clampeado, acodado u obstruido. Revisa el circuito y avisa.' },
      } },
    { k: 'dve_debito', label: 'Débito', tipo: 'select', dependeDe: { campo: 'dve', en: ['Con DVE'] },
      opciones: ['Sin débito', 'Escaso', 'Moderado', 'Abundante'],
      notas: {
        'Sin débito': { nivel: 'leve',     texto: 'Correlaciona con la oscilación: sin débito Y sin oscilación sugiere obstrucción.' },
        'Abundante':  { nivel: 'moderado', texto: 'Drenaje alto: vigila sobredrenaje y compara con el límite indicado, avisa.' },
      } },
    { k: 'dve_debito_ml', label: 'Débito del turno', tipo: 'num', unidad: 'ml', dependeDe: { campo: 'dve', en: ['Con DVE'] } },
    { k: 'dve_lcr', label: 'Aspecto del LCR', tipo: 'select', dependeDe: { campo: 'dve', en: ['Con DVE'] },
      opciones: ['Claro', 'Xantocrómico', 'Hemático', 'Turbio'],
      notas: {
        'Claro':        { nivel: 'normal',   texto: 'Aspecto esperado.' },
        'Xantocrómico': { nivel: 'leve',     texto: 'Amarillento: sangrado previo en degradación.' },
        'Hemático':     { nivel: 'moderado', texto: 'Sangre fresca: avisa y correlaciona con la imagen.' },
        'Turbio':       { nivel: 'moderado', texto: 'Turbio: piensa en ventriculitis. Avisa y correlaciona con cultivos de LCR.' },
      } },
  ],
  // ── Traqueostomía: lo que se MIRA en el cuello ───────────────────────────
  // Mismo criterio con el que el DVE se mudó a cabeza: el detalle que se
  // constata mirando al paciente vive en el examen, no en la evolución.
  // Frontera deliberada para no pedir el dato dos veces: el DÍA, el TIPO
  // (percutánea/quirúrgica), la CÁNULA y cómo ventila hoy ya viven en la rama
  // de ventilación de la evolución (traqueoDia/traqueoTipo/traqueoCanula/
  // tqtSoporte) y en el dispositivo declarado — aquí NO se repiten. Lo que no
  // existía en ninguna parte, y es cuidado de enfermería puro, es el ESTOMA:
  // si se ve vital, qué secreción tiene, si conserva los puntos y cómo está
  // fijada la cánula.
  cuello: [
    { k: 'tqt_estoma', label: 'Estoma', tipo: 'select',
      opciones: ['Vital, sin signos inflamatorios', 'Eritematoso', 'Con granuloma', 'Con signos de infección', 'Con sangrado', 'Con palidez o necrosis del borde'],
      notas: {
        'Vital, sin signos inflamatorios': { nivel: 'normal',   texto: 'Bordes rosados y bien perfundidos, sin exudado: aspecto esperado.' },
        Eritematoso:                       { nivel: 'leve',     texto: 'Eritema peristomal: revisa humedad y fijación, y sube la frecuencia de curación.' },
        'Con granuloma':                   { nivel: 'leve',     texto: 'Tejido de granulación en el borde: sangra con facilidad al manipular. Regístralo y avisa.' },
        'Con signos de infección':         { nivel: 'moderado', texto: 'Eritema con calor, dolor o exudado purulento: avisa y considera cultivo según protocolo.' },
        'Con sangrado':                    { nivel: 'moderado', texto: 'Sangrado por el estoma: cuantifícalo y avisa. Un sangrado abundante o pulsátil es urgencia.' },
        'Con palidez o necrosis del borde': { nivel: 'severo',  texto: 'Borde pálido, violáceo o necrótico: compromiso de perfusión del estoma, avisa de inmediato.' },
      } },
    { k: 'tqt_secrecion', label: 'Secreción del estoma', tipo: 'select',
      opciones: ['Sin secreción', 'Escasa serosa', 'Abundante serosa', 'Purulenta', 'Hemática'],
      notas: {
        'Sin secreción':     { nivel: 'normal',   texto: 'Esperado.' },
        'Escasa serosa':     { nivel: 'normal',   texto: 'Secreción serosa escasa: habitual, mantén el apósito seco.' },
        'Abundante serosa':  { nivel: 'leve',     texto: 'Humedad mantenida alrededor del estoma: cambia el apósito con más frecuencia, es la puerta de la maceración.' },
        Purulenta:           { nivel: 'moderado', texto: 'Secreción purulenta: piensa en infección del estoma, avisa.' },
        Hemática:            { nivel: 'moderado', texto: 'Secreción con sangre: correlaciona con las aspiraciones y avisa.' },
      } },
    // "Si tiene puntos" (dictado): en la percutánea muchas veces no hay, en la
    // quirúrgica se retiran a los días. Las cuatro respuestas son constataciones
    // distintas y ninguna es "falta un dato".
    { k: 'tqt_puntos', label: 'Puntos de sutura', tipo: 'select',
      opciones: ['Sin puntos', 'In situ, indemnes', 'In situ, alguno suelto', 'Retirados'],
      notas: {
        'In situ, indemnes':      { nivel: 'normal',   texto: 'Puntos íntegros: la cánula conserva su fijación quirúrgica.' },
        'In situ, alguno suelto': { nivel: 'moderado', texto: 'Punto suelto: refuerza la fijación externa y avisa, la cánula depende de ella.' },
      } },
    { k: 'tqt_fijacion', label: 'Fijación de la cánula', tipo: 'select',
      opciones: ['Cintas bien ajustadas', 'Cintas holgadas', 'Cintas muy ajustadas', 'Fijada con sutura a piel'],
      notas: {
        'Cintas bien ajustadas': { nivel: 'normal',   texto: 'La referencia es que pase un dedo entre la cinta y el cuello.' },
        'Cintas holgadas':       { nivel: 'moderado', texto: 'Riesgo de decanulación accidental: reajusta dejando un dedo de holgura.' },
        'Cintas muy ajustadas':  { nivel: 'moderado', texto: 'Riesgo de lesión por presión bajo la cinta y la placa: reajusta dejando un dedo de holgura.' },
        'Fijada con sutura a piel': { nivel: 'normal', texto: 'Habitual en la traqueostomía reciente: vigila los puntos y la piel bajo la placa.' },
      } },
    { k: 'tqt_cuff', label: 'Cuff de la cánula', tipo: 'num', unidad: 'cmH₂O', bandas: BANDAS_CUFF },
  ],
  torax: [
    { k: 'patron_resp', label: 'Patrón respiratorio', tipo: 'select',
      opciones: ['Eupneico', 'Taquipneico', 'Bradipneico', 'Superficial', 'Irregular'],
      notas: {
        Eupneico:    { nivel: 'normal', texto: 'Esperado.' },
        Taquipneico: { nivel: 'moderado', texto: 'Correlaciona con FR, SpO₂ y trabajo respiratorio.' },
        Bradipneico: { nivel: 'moderado', texto: 'Correlaciona con sedación y nivel de conciencia.' },
        Superficial: { nivel: 'moderado', texto: 'Volumen corriente bajo: vigilar fatiga.' },
        Irregular:   { nivel: 'moderado', texto: 'Patrón irregular: correlacionar con neuro.' },
      } },
    // Auscultación con didáctica por opción (misma regla que la evolución):
    // la nota de cada marcada se muestra y enseña mientras se documenta.
    { k: 'auscultacion', label: 'Auscultación', tipo: 'multi',
      opciones: ['Murmullo pulmonar conservado', 'Murmullo pulmonar disminuido', 'Murmullo pulmonar ausente', 'Crépitos', 'Sibilancias'],
      notas: {
        'Murmullo pulmonar conservado':  { nivel: 'normal', texto: 'Esperado: aire entrando bien en ambos campos.' },
        'Murmullo pulmonar disminuido':  { nivel: 'moderado', texto: 'Menos entrada de aire: correlaciona con atelectasia, derrame u obstrucción.' },
        'Murmullo pulmonar ausente':     { nivel: 'severo', texto: 'Ausente unilateral es emergencia ventilatoria: avisar de inmediato.' },
        'Crépitos':                      { nivel: 'moderado', texto: 'Sugieren líquido o apertura alveolar: correlaciona con balance y oxigenación.' },
        'Sibilancias':                   { nivel: 'moderado', texto: 'Broncoobstrucción: correlaciona con broncodilatadores indicados.' },
      } },
  ],
  abdomen: [
    { k: 'rha', label: 'Ruidos hidroaéreos', tipo: 'select',
      opciones: ['Presentes', 'Aumentados', 'Disminuidos', 'Ausentes'],
      notas: {
        Presentes:   { nivel: 'normal', texto: 'Esperado.' },
        Aumentados:  { nivel: 'leve', texto: 'Correlaciona con tránsito y deposiciones.' },
        Disminuidos: { nivel: 'moderado', texto: 'Vigilar tránsito, distensión y tolerancia.' },
        Ausentes:    { nivel: 'severo', texto: 'Con distensión, sugiere íleo: avisar.' },
      } },
    { k: 'blandura', label: 'A la palpación', tipo: 'select',
      opciones: ['Blando y depresible', 'Distendido', 'Doloroso a la palpación', 'En tabla'],
      notas: {
        'Blando y depresible':     { nivel: 'normal', texto: 'Esperado.' },
        Distendido:                { nivel: 'moderado', texto: 'Correlaciona con RHA y tránsito.' },
        'Doloroso a la palpación': { nivel: 'moderado', texto: 'Localiza el dolor y correlaciona con analgesia indicada.' },
        'En tabla':                { nivel: 'severo', texto: 'Abdomen en tabla con dolor: avisar de inmediato.' },
      } },
    // Percusión (dictado): el timpanismo ES el sonido hueco; la matidez es el
    // apagado. La nota enseña el término mientras se documenta.
    { k: 'percusion', label: 'A la percusión', tipo: 'select',
      opciones: ['Timpánico', 'Matidez'],
      notas: {
        Timpánico: { nivel: 'normal', texto: 'Sonido hueco: aire en asas, lo esperado.' },
        Matidez:   { nivel: 'moderado', texto: 'Sonido apagado: sugiere líquido o masa, correlaciona con distensión y balance.' },
      } },
  ],
  eess: [
    { k: 'pulsos', label: 'Pulsos', tipo: 'select',
      opciones: ['Presentes y simétricos', 'Disminuidos', 'Asimétricos', 'Ausentes'],
      notas: {
        'Presentes y simétricos': { nivel: 'normal', texto: 'Esperado: radial y braquial presentes.' },
        Disminuidos:              { nivel: 'moderado', texto: 'Correlaciona con perfusión, PA y temperatura distal.' },
        'Asimétricos':            { nivel: 'moderado', texto: 'Compara con el contralateral y con el basal: avisar si es nuevo.' },
        Ausentes:                 { nivel: 'severo', texto: 'Pulso ausente es bandera vascular: avisar de inmediato.' },
      } },
    { k: 'llene_capilar_seg', label: 'Llene capilar', tipo: 'num', unidad: 's', bandas: [
      { hasta: 3,  texto: 'Perfusión distal esperada (≤3 s).', nivel: 'normal' },
      { hasta: 99, texto: 'Enlentecido (>3 s): correlaciona con perfusión.', nivel: 'moderado' },
    ] },
    { k: 'fuerza', label: 'Fuerza', tipo: 'select', opciones: ['Conservada', 'Paresia', 'Plejia'],
      notas: {
        Conservada: { nivel: 'normal', texto: 'Esperado.' },
        Paresia:    { nivel: 'moderado', texto: 'Focalidad: objetivar y comparar con el basal.' },
        Plejia:     { nivel: 'severo', texto: 'Focalidad nueva es bandera neurológica: avisar.' },
      } },
  ],
  eeii: [
    { k: 'pulsos', label: 'Pulsos', tipo: 'select',
      opciones: ['Presentes y simétricos', 'Disminuidos', 'Asimétricos', 'Ausentes'],
      notas: {
        'Presentes y simétricos': { nivel: 'normal', texto: 'Esperado: palpa pedio y tibial posterior; si no están, busca el poplíteo.' },
        Disminuidos:              { nivel: 'moderado', texto: 'Correlaciona con perfusión, PA y temperatura distal.' },
        'Asimétricos':            { nivel: 'moderado', texto: 'Compara con el contralateral y con el basal: avisar si es nuevo.' },
        Ausentes:                 { nivel: 'severo', texto: 'Pulso ausente es bandera vascular: avisar de inmediato.' },
      } },
    { k: 'llene_capilar_seg', label: 'Llene capilar', tipo: 'num', unidad: 's', bandas: [
      { hasta: 3,  texto: 'Perfusión distal esperada (≤3 s).', nivel: 'normal' },
      { hasta: 99, texto: 'Enlentecido (>3 s): correlaciona con perfusión.', nivel: 'moderado' },
    ] },
    { k: 'fuerza', label: 'Fuerza', tipo: 'select', opciones: ['Conservada', 'Paresia', 'Plejia'],
      notas: {
        Conservada: { nivel: 'normal', texto: 'Esperado.' },
        Paresia:    { nivel: 'moderado', texto: 'Focalidad: objetivar y comparar con el basal.' },
        Plejia:     { nivel: 'severo', texto: 'Focalidad nueva es bandera neurológica: avisar.' },
      } },
  ],
  genitales: [
    // La DAI se localiza (dictado 2026-07-18): inguinal, perineal, perianal o
    // irradiada. La nota fija la distinción clave: humedad no es presión.
    { k: 'dai_localizacion', label: 'Localización de la DAI', tipo: 'multi',
      opciones: ['Inguinal', 'Perineal', 'Perianal', 'Irradiada a glúteos'],
      nota: { nivel: 'info', texto: 'La DAI es daño por HUMEDAD (difusa, en zonas de contacto); la LPP es por PRESIÓN (sobre prominencia ósea). Distinguirlas cambia el manejo.' } },
  ],
  sacra: [
    { k: 'lpp_categoria', label: 'LPP', tipo: 'select',
      opciones: ['Categoría I', 'Categoría II', 'Categoría III', 'Categoría IV', 'DTPI', 'No estadiable'],
      notas: {
        'Categoría I':   { nivel: 'leve', texto: 'Eritema que no blanquea, piel íntegra: prevención inmediata.' },
        'Categoría II':  { nivel: 'moderado', texto: 'Pérdida parcial del grosor: curación según protocolo.' },
        'Categoría III': { nivel: 'severo', texto: 'Pérdida total del grosor: avisar y manejar según protocolo.' },
        'Categoría IV':  { nivel: 'severo', texto: 'Compromiso profundo: avisar y manejar según protocolo.' },
        DTPI:            { nivel: 'moderado', texto: 'Lesión de tejido profundo en evolución: vigilar de cerca.' },
        'No estadiable': { nivel: 'moderado', texto: 'Lecho no visible: estadificar tras desbridamiento según protocolo.' },
      } },
    { k: 'lpp_localizacion', label: 'Localización LPP', tipo: 'select',
      opciones: ['Sacro', 'Talón', 'Isquion', 'Trocánter', 'Occipucio', 'Otra zona de apoyo'] },
  ],
};

// Examen SUELTO (MiPAE en turno por partes, 2026-10-05, docs/plan-turno-por-partes.md):
// la pieza "Examen físico" se usa fuera del turno, sin recibir a nadie, así que
// su texto no puede decir "al recibir". La marca vive en el mismo objeto
// (`suelto: true`, clave opcional) para que TODAS las funciones que ya arman el
// texto la lean sin cambiar de firma: buildExamenFisico, lineasPiel y el pulido
// reciben el mismo objeto de siempre. Sin la clave el texto es IDÉNTICO al de
// hoy, fijado por test. Solo la escribe la pieza, que no guarda nada.
export function esExamenSuelto(ex) {
  return ex?.suelto === true;
}

// Los chips del catálogo que nombran el turno. El DATO no cambia (sigue siendo
// el valor de catálogo, así un examen suelto y uno del turno guardan lo mismo);
// cambia solo cómo se dice fuera del turno, en el chip y en el texto, con la
// MISMA función en los dos lados.
const ETIQUETA_SUELTA = Object.freeze({ 'LPP presente al recibir': 'LPP presente' });
export function etiquetaHallazgoExamen(v, ex) {
  return esExamenSuelto(ex) && ETIQUETA_SUELTA[v] ? ETIQUETA_SUELTA[v] : v;
}

export function estadoInicialExamen() {
  return {
    // Hora del sistema al aterrizar el PRIMER dato (marcarHoraExamen). No
    // editable por diseño: es la evidencia de "examen temprano".
    hora: null, // 'HH:MM'
    ts: null,   // epoch ms
    impresion: null,
    conciencia: null,
    posicion: null,
    // Signos de contexto de la foto (opcionales): { fc, pas, pad, ... }.
    signos: {},
    // Evaluación primaria ABCDE (opcional): { a, b, c, d, e } con
    // 'ok' | 'alterado' | ausente (= no evaluada).
    abcde: {},
    // Escalas baseline calculadas desde el examen (opcionales):
    // { [escalaId]: vals } — mismo shape que las escalas de la evolución,
    // mismo motor (scoreEscala), cero re-derivación de cortes.
    escalas: {},
    // { [segmentoId]: { sinHallazgos, hallazgos: string[], nota: string,
    //                   campos: { [k]: valor } } }
    segmentos: {},
  };
}

// Estado de un segmento, tolerante a claves ausentes (mismo patrón que
// sistemaState de la evolución). Un draft viejo sin `campos` no rompe.
export function segmentoExamenState(ex, id) {
  const s = ex?.segmentos?.[id] || {};
  return {
    sinHallazgos: Boolean(s.sinHallazgos),
    hallazgos: Array.isArray(s.hallazgos) ? s.hallazgos : [],
    nota: typeof s.nota === 'string' ? s.nota : '',
    campos: s.campos && typeof s.campos === 'object' ? s.campos : {},
    // Partes marcadas normal (dictado 2026-07-18): grupos de chips cuyo estado
    // basal el usuario afirmó ("normocráneo"). Clave opcional, retrocompatible.
    gruposNormales: Array.isArray(s.gruposNormales) ? s.gruposNormales : [],
  };
}

// ── Soportes al recibir (dictado del fundador, 2026-08-01) ────────────────────
// Antes del examen céfalo-caudal, tres preguntas breves: ¿sedado y con qué?,
// ¿con qué soporte respiratorio?, ¿con drogas vasoactivas y cuáles? Son la
// foto de los soportes al tomar la cama, y DESPUÉS viajan a la evolución por
// sistema (mergeSoportesEnEvolucion siembra los campos de neuro/ventilación/
// hemodinamia). Las opciones se DERIVAN de CAMPOS_POR_SISTEMA — mismo string,
// misma fuente, cero catálogo paralelo (norma de máxima coherencia) — así el
// puente es identidad 1:1. Catálogo cerrado: PII-safe por construcción.
// Clave opcional `examen.soportes` en el mismo jsonb (retrocompatible: sin la
// clave, output idéntico — regla fijada por test).
const campoEvolucion = (sistema, k) =>
  (CAMPOS_POR_SISTEMA[sistema] || []).find((c) => c.k === k) || {};

// Raíz de hemodinamia del chequeo (dictado 2026-08-09): vive SOLO en
// examen.soportes y entra a la prosa vía lineaSoportes, NO viaja a
// evolucion.sistemas (mismo precedente que ex.conciencia: dato de la foto de
// recepción, no de la película). Catálogo cerrado: el porqué "Otro" jamás
// abre texto libre. El catálogo del porqué es BORRADOR del fundador a validar.
export const HEMO_ESTADO = ['Estable', 'Inestable'];
export const HEMO_INESTABLE_PORQUE = [
  { id: 'hipotension', label: 'Hipotensión sostenida', prosa: 'hipotensión sostenida' },
  { id: 'dva_en_aumento', label: 'Requerimiento de DVA en aumento', prosa: 'requerimiento de DVA en aumento' },
  { id: 'arritmia', label: 'Arritmia con compromiso', prosa: 'arritmia con compromiso hemodinámico' },
  { id: 'sangrado', label: 'Sangrado activo', prosa: 'sangrado activo' },
  { id: 'otro', label: 'Otro', prosa: null },
];

export const SOPORTES_EXAMEN = {
  sedacion:      campoEvolucion('neuro', 'sedacion').opciones || [],
  sedoanalgesia: campoEvolucion('neuro', 'sedoanalgesia').opciones || [],
  // BNM y TOF (dictado 2026-08-01: "si tiene BIS agregar valor, y TOF para
  // bloqueo"); el BIS es número libre, estos dos derivan de la rama.
  bnm: campoEvolucion('neuro', 'bnm').opciones || [],
  tof: campoEvolucion('neuro', 'tof').opciones || [],
  // Dolor al recibir (dictado 2026-08-01, v2 "cómo llega tu paciente"):
  // mismas opciones que la rama de la evolución; se objetiva con EVA/CPOT
  // (escalas del examen) y el manejo va en texto breve (mismo campo).
  dolor: campoEvolucion('neuro', 'dolor').opciones || [],
  // Un solo select breve que resume la rama de vía aérea: los soportes de la
  // nativa + los dispositivos de la artificial (el puente los reparte).
  respiratorio: [
    ...(campoEvolucion('ventilacion', 'soporte').opciones || []),
    ...(campoEvolucion('ventilacion', 'viaArtificial').opciones || []),
  ],
  // Con vía artificial, el modo en una palabra (mismas opciones que vmiModo:
  // "casi como el sistema pero más acotado", dictado del fundador).
  vmiBreve:   campoEvolucion('ventilacion', 'vmiModo').opciones || [],
  // Detalle BREVE por soporte (dictado 2026-08-01, 10:48: "no hay detalle
  // sobre oxigenoterapia, ni VMI ni VNI"): interfaz de O2 y modo de VNI
  // derivan de la rama; los números (flujo, FiO2, presiones, PEEP) se guardan
  // con las MISMAS claves de la evolución y siembran por identidad.
  o2Interfaz: campoEvolucion('ventilacion', 'o2Interfaz').opciones || [],
  vniModo:    campoEvolucion('ventilacion', 'vniModo').opciones || [],
  dvaSoporte: campoEvolucion('hemodinamia', 'dvaSoporte').opciones || [],
  dva:        campoEvolucion('hemodinamia', 'dva').opciones || [],
};

// Los soportes de vía aérea NATIVA (para que el puente sepa a qué lado de la
// rama va cada opción del select breve).
// Exportado: `ventilacionSoloEco` (visitaEnfermeria.js) necesita la MISMA
// frontera nativa/artificial que usa la siembra chequeo→evolución de aquí
// abajo — dos copias de esta lista se desalinearían en silencio.
export const RESPIRATORIO_NATIVO = campoEvolucion('ventilacion', 'soporte').opciones || [];

export function soportesExamenState(ex) {
  return ex?.soportes && typeof ex.soportes === 'object' ? ex.soportes : {};
}

// Signos / ABCDE / escalas, tolerantes a drafts viejos que no traen la clave.
export function signosExamenState(ex) {
  return ex?.signos && typeof ex.signos === 'object' ? ex.signos : {};
}
export function abcdeExamenState(ex) {
  return ex?.abcde && typeof ex.abcde === 'object' ? ex.abcde : {};
}
export function escalasExamenState(ex) {
  return ex?.escalas && typeof ex.escalas === 'object' ? ex.escalas : {};
}

const hayValor = (v) => v !== undefined && v !== null && String(v).trim() !== '';

function segmentoConContenido(ex, seg) {
  const { sinHallazgos, hallazgos, nota, campos, gruposNormales } = segmentoExamenState(ex, seg.id);
  return sinHallazgos || hallazgos.length > 0 || nota.trim().length > 0 || gruposNormales.length > 0
    || Object.values(campos).some((v) => Array.isArray(v) ? v.length > 0 : hayValor(v));
}

export function examenTieneContenido(ex) {
  if (!ex) return false;
  if (ex.impresion || ex.conciencia || ex.posicion) return true;
  // Los datos del modo detallado también son contenido: un signo o una escala
  // baseline ya es "el examen se hizo" (y sella la hora — mismo contrato).
  if (Object.values(signosExamenState(ex)).some(hayValor)) return true;
  if (Object.values(abcdeExamenState(ex)).some(hayValor)) return true;
  // Los soportes al recibir también son contenido (y sellan la hora: hubo
  // examen). Las claves de dosis son MAPAS ({droga: dosis}): un objeto vacío
  // no cuenta (si contara, sellaría la hora sin dato real).
  if (Object.values(soportesExamenState(ex)).some((v) => (
    Array.isArray(v) ? v.length > 0
      : v && typeof v === 'object' ? Object.values(v).some(hayValor)
        : hayValor(v)
  ))) return true;
  if (Object.entries(escalasExamenState(ex)).some(([id, vals]) => escalaConValores(id, vals))) return true;
  // La piel también es contenido: "piel indemne" o una lesión al recibir son
  // constataciones del examen y sellan la hora si son lo primero registrado
  // (correcto: hubo examen). Cada lesión/revisión lleva además su propia hora,
  // así que la trazabilidad LPP no depende de la hora global.
  if (pielTieneContenido(ex)) return true;
  return SEGMENTOS_EXAMEN.some((seg) => segmentoConContenido(ex, seg));
}

// Completitud por segmento (motor del semáforo y del anillo, como en la
// evolución). "Con datos" = evaluado (normal o con hallazgos). Una lesión
// marcada en el MAPA también enciende su segmento (mapa y examen son la misma
// constatación — marcar en uno no deja al otro "vacío"); el texto no cambia:
// la lesión ya se imprime en su línea de piel, no como línea de segmento.
// `pielDe` (opcional, default el mismo examen): de qué examen leer el mapa
// corporal. El examen de CONTROL no copia piel a propósito (el mapa es UNO,
// vive en el principal), así que sin esto sus segmentos con LPP documentada
// aparecían "sin constatar" y "El resto, sin hallazgos" podía firmar "piel
// indemne" encima de una lesión vigente (auditoría 2026-07-27).
export function completitudExamen(ex, pielDe = ex) {
  const conLesion = segmentosConLesion(pielDe);
  const porSegmento = SEGMENTOS_EXAMEN.map((seg) => ({
    id: seg.id,
    label: seg.label,
    conDatos: segmentoConContenido(ex, seg) || conLesion.has(seg.id),
  }));
  const conDatos = porSegmento.filter((x) => x.conDatos).length;
  const total = SEGMENTOS_EXAMEN.length;
  return { porSegmento, conDatos, total, pct: total ? Math.round((conDatos / total) * 100) : 0 };
}

// ── "El resto, sin hallazgos" (examen expedito) ───────────────────────────────
// El flujo mental real al salir de la pieza es "traigo 2 cosas raras en la
// cabeza": se registran esas 2 y el resto se firma de un toque. Restantes =
// segmentos SIN contenido y SIN lesión del mapa (una lesión enciende su
// segmento: firmarle "sin hallazgos" encima lo contradiría). El invariante no
// se toca: la UI muestra las frases basales ANTES de firmar — el usuario firma
// lo que constató, no un checkbox ciego.
export function segmentosSinConstatar(ex, pielDe = ex) {
  const { porSegmento } = completitudExamen(ex, pielDe);
  return porSegmento.filter((s) => !s.conDatos).map(({ id, label }) => ({ id, label }));
}

// Declara "sin hallazgos" en un conjunto de segmentos (por id). Pura; devuelve
// el MISMO objeto si la lista viene vacía. La firma sigue siendo consciente:
// quien llama muestra las frases basales ANTES (invariante intocado). Se usa
// tanto para "el resto" completo como para el modo dictado, donde la enfermera
// destilda antes de firmar el segmento que no constató.
export function firmarSegmentosSinHallazgos(ex, ids) {
  const lista = Array.isArray(ids) ? ids.filter(Boolean) : [];
  if (!lista.length) return ex;
  const segmentos = { ...(ex?.segmentos || {}) };
  for (const id of lista) {
    segmentos[id] = { ...(segmentos[id] || {}), sinHallazgos: true };
  }
  return { ...ex, segmentos };
}

// Declara "sin hallazgos" en todos los restantes. Pura; devuelve el MISMO
// objeto si no queda nada por firmar.
export function firmarRestoSinHallazgos(ex, pielDe = ex) {
  const restantes = segmentosSinConstatar(ex, pielDe);
  if (!restantes.length) return ex;
  return firmarSegmentosSinHallazgos(ex, restantes.map((s) => s.id));
}

// ── Piel indemne al ingreso (una sola fuente) ────────────────────────────────
// El camino normal de la piel es UN toque: registra la revisión de ingreso y
// deja coherente el recorrido céfalo-caudal (espalda/sacra en "sin hallazgos"
// si estaban vacíos — cero doble trabajo). Vivía dentro de PielPanel; ahora es
// dominio puro porque DOS superficies la ejecutan (el módulo "Estado de la
// piel" arriba del cuerpo y el propio panel) y una lógica médico-legal así no
// puede existir dos veces.
// Vive en este módulo y no en pielLpp porque toca las dos mitades del examen
// (la piel y los segmentos): examenFisico ya importa a pielLpp, al revés sería
// un ciclo.
// Idempotente a propósito: `conRevision` no dedupea, así que dos toques del
// mismo botón dejarían dos constataciones de lo mismo. Con la revisión ya
// registrada devuelve el MISMO objeto (toggle-safe).
export function marcarPielIndemne(ex, { ahora = new Date() } = {}) {
  const base = ex || {};
  if (pielIndemneAlIngreso(base)) return ex;
  const conRev = conRevision(base, nuevaRevision({ contexto: 'ingreso', resultado: 'indemne', ahora }));
  const segmentos = { ...(conRev.segmentos || {}) };
  for (const segId of ['espalda', 'sacra']) {
    const s = segmentoExamenState(conRev, segId);
    if (!s.sinHallazgos && !s.hallazgos.length && !s.nota.trim() && !Object.keys(s.campos).length) {
      segmentos[segId] = { ...(segmentos[segId] || {}), sinHallazgos: true, hallazgos: [], nota: s.nota, campos: s.campos };
    }
  }
  return { ...conRev, segmentos };
}

// Sella hora + ts la PRIMERA vez que el examen tiene contenido. Idempotente:
// si ya hay hora, no la toca. La `hora` luego puede corregirla el autor desde
// la UI (hora real del examen); el `ts` del sistema NO se edita nunca — esa
// inmutabilidad es el valor para jefatura. Pura: no muta `ex`.
export function marcarHoraExamen(ex, ahora = new Date()) {
  if (!ex || ex.hora || !examenTieneContenido(ex)) return ex;
  const h = String(ahora.getHours()).padStart(2, '0');
  const m = String(ahora.getMinutes()).padStart(2, '0');
  return { ...ex, hora: `${h}:${m}`, ts: ahora.getTime() };
}

// Minutos entre la creación del registro (recibir la cama) y el sello del
// examen — LA métrica de "examen temprano" para jefatura. null si falta algo.
export function minutosHastaExamen(registroCreado, ex) {
  if (!registroCreado || !ex?.ts) return null;
  const min = Math.round((ex.ts - registroCreado) / 60000);
  return min >= 0 ? min : null;
}

// ── Examen de control (dictado del fundador, 2026-07-27) ─────────────────────
// Durante el turno se puede repetir el examen TOMANDO EL ANTERIOR COMO BASE:
// parte pre-llenado con lo constatado al recibir y el autor corrige solo lo
// que cambió. Vive como clave opcional `control` del MISMO jsonb
// (examen_fisico.control), retrocompatible: sin la clave, ningún output
// cambia (regla fijada por test, como todas las claves nuevas del examen).
// NO copia piel (el mapa corporal es UNO y continuo: vive en el examen
// principal y su control intra-turno ya es ControlPielCard), ni signos, ni
// ABCDE, ni escalas (se re-evalúan si corresponde), ni la nota libre por
// segmento (no viaja — solo catálogo). La hora/ts del control se sellan al
// CREARLO: la copia ES el primer dato, y la hora sigue siendo corregible por
// el autor (mismos dos tiempos del examen de ingreso).
export function iniciarExamenControl(base, ahora = new Date()) {
  const b = base || estadoInicialExamen();
  const segmentos = {};
  for (const [id, s] of Object.entries(b.segmentos || {})) {
    if (!s || typeof s !== 'object') continue;
    segmentos[id] = {
      sinHallazgos: Boolean(s.sinHallazgos),
      hallazgos: Array.isArray(s.hallazgos) ? [...s.hallazgos] : [],
      gruposNormales: Array.isArray(s.gruposNormales) ? [...s.gruposNormales] : [],
      campos: s.campos && typeof s.campos === 'object' ? JSON.parse(JSON.stringify(s.campos)) : {},
    };
  }
  const h = String(ahora.getHours()).padStart(2, '0');
  const m = String(ahora.getMinutes()).padStart(2, '0');
  return {
    ...estadoInicialExamen(),
    hora: `${h}:${m}`,
    ts: ahora.getTime(),
    impresion: b.impresion || null,
    conciencia: b.conciencia || null,
    posicion: b.posicion || null,
    segmentos,
  };
}

// El examen VIGENTE de la ficha: el de control si tiene contenido, si no el
// de ingreso. La evolución habla del estado ACTUAL del paciente, así que sus
// puentes (mergeExamenEnEvolucion, memoria por sistema) leen de aquí. La
// visita de enfermería, en cambio, sigue leyendo el de ingreso: esa foto se
// tomó en conjunto con el TENS y no se reescribe.
export function examenVigenteDe(ex) {
  const ctrl = ex?.control;
  return ctrl && examenTieneContenido(ctrl) ? ctrl : (ex || null);
}

// Minúscula inicial respetando siglas (LPP, CUP…): si la primera palabra trae
// mayúscula interna, se deja tal cual (mismo heurístico de evolucionSistema).
const esSigla = (t) => /[A-ZÁÉÍÓÚÑ]/.test((t || '').split(' ')[0].slice(1));
const lowerFirst = (t) => (!t || esSigla(t) ? t : t.charAt(0).toLowerCase() + t.slice(1));

// ── Texto del examen ──────────────────────────────────────────────────────────
// ── Descripción basal por segmento ────────────────────────────────────────────
// Lo que "Sin hallazgos" DECLARA, escrito una vez y bien (decisión del
// fundador, 2026-07-11): un examen normal registrado como "sin hallazgos" a
// secas es débil como documento — el texto debe describir lo básico que se
// constató, como se evoluciona de verdad. Marcar el chip firma esta frase (la
// UI la muestra antes de marcar). Catálogo cerrado: PII-safe por construcción,
// viaja al PAE y a la evolución igual que los hallazgos.
export const NORMAL_SEGMENTO = {
  cabeza: 'normocéfalo, pupilas isocóricas con reflejo fotomotor presente, facies simétrica, mucosas rosadas e hidratadas, sin lesiones',
  cuello: 'simétrico y móvil, sin adenopatías palpables ni ingurgitación yugular',
  // Ojo cohesión: "piel indemne" vive solo en los segmentos donde la piel ES
  // el punto (genitales/espalda/sacra) — repetirla en tórax y extremidades
  // dejaba el texto machacón (feedback del fundador, 2026-07-11).
  torax: 'simétrico, expansión conservada, sin uso de musculatura accesoria. A la auscultación, murmullo pulmonar presente bilateral sin ruidos agregados',
  abdomen: 'blando, depresible, indoloro a la palpación, sin distensión, ruidos hidroaéreos presentes',
  eess: 'simétricas y móviles, sin edema, llene capilar conservado',
  eeii: 'simétricas, sin edema, pulsos distales presentes, llene capilar conservado',
  genitales: 'piel y mucosas indemnes, sin lesiones ni secreciones',
  espalda: 'piel indemne, sin lesiones ni zonas de eritema',
  sacra: 'piel indemne, zonas de apoyo sin eritema ni signos de presión',
};

// Una línea por segmento EVALUADO ("Cabeza y cara: normocéfalo, …" /
// "Zona sacra y puntos de apoyo: eritema que no blanquea. <nota del autor>").
// Los segmentos no evaluados se omiten: el silencio dice "no evaluado", nunca
// se inventa normalidad (misma regla que la evolución). La nota libre va tal
// cual — es la voz del autor. `incluirNotas: false` produce la variante
// PII-safe (solo catálogo y números) que viaja al prefill del PAE, y
// `incluirNotas: 'seguras'` la variante del documento de la VISITA (incidente
// Bupa, dictado del fundador 2026-08-18, "¡los brazos!"): la nota entra SOLO
// si pasa la guarda DURA de PII — excluirlas todas amputaba en silencio el
// hallazgo más crítico del examen (una línea arterial sangrante) del
// documento que la enfermera copia a la ficha institucional.

// Línea de signos: solo los presentes, en el orden del catálogo. PA se compone
// de PAS/PAD y la PAM se deriva con el motor de escalas (scorePam — mismos
// cortes, cero re-derivación). FiO₂ acompaña a la SpO₂ entre paréntesis.
function signosTexto(ex) {
  const s = signosExamenState(ex);
  const partes = [];
  if (hayValor(s.fc)) partes.push(`FC ${s.fc} lpm`);
  if (hayValor(s.pas) && hayValor(s.pad)) {
    const pam = scoreEscala('pam', { pas: s.pas, pad: s.pad });
    partes.push(`PA ${s.pas}/${s.pad} mmHg${pam ? ` (PAM ${pam.label})` : ''}`);
  } else if (hayValor(s.pas) || hayValor(s.pad)) {
    partes.push(`PA ${hayValor(s.pas) ? s.pas : '—'}/${hayValor(s.pad) ? s.pad : '—'} mmHg`);
  }
  if (hayValor(s.fr)) partes.push(`FR ${s.fr} rpm`);
  if (hayValor(s.spo2)) partes.push(`SpO₂ ${s.spo2}%${hayValor(s.fio2) ? ` (FiO₂ ${s.fio2}%)` : ''}`);
  else if (hayValor(s.fio2)) partes.push(`FiO₂ ${s.fio2}%`);
  if (hayValor(s.temp)) partes.push(`T° ${s.temp} °C`);
  if (hayValor(s.hgt)) partes.push(`HGT ${s.hgt} mg/dL`);
  if (hayValor(s.dolor_eva)) partes.push(`dolor EVA ${s.dolor_eva}/10`);
  return partes.length ? `${esExamenSuelto(ex) ? 'Signos vitales' : 'Signos al recibir'}: ${partes.join(', ')}.` : '';
}

// Línea ABCDE: solo las letras evaluadas; 'alterado' se lee fuerte a propósito
// (es la bandera). El silencio de una letra dice "no evaluada".
function abcdeTexto(ex) {
  const a = abcdeExamenState(ex);
  const partes = ABCDE_EXAMEN
    .filter((l) => a[l.k] === 'ok' || a[l.k] === 'alterado')
    .map((l) => `${l.letra} ${a[l.k] === 'ok' ? 'sin alteración' : 'ALTERADA'}`);
  return partes.length ? `Evaluación primaria ABCDE: ${partes.join(', ')}.` : '';
}

// Prosa de un campo estructurado ("pupilas 3 mm", "LPP: categoría II",
// "auscultación: crépitos, sibilancias"). Solo catálogo y números — PII-safe
// por construcción.
function campoExamenTexto(def, valor) {
  if (def.tipo === 'num') return `${lowerFirst(def.label)} ${valor} ${def.unidad}`;
  if (def.tipo === 'multi') {
    return Array.isArray(valor) && valor.length
      ? `${lowerFirst(def.label)}: ${valor.map(lowerFirst).join(', ')}`
      : '';
  }
  return `${lowerFirst(def.label)}: ${lowerFirst(String(valor))}`;
}

function camposSegmentoTexto(segId, campos, claves = null) {
  const defs = CAMPOS_EXAMEN[segId] || [];
  const partes = [];
  for (const def of defs) {
    if (claves && !claves.includes(def.k)) continue;
    // Un campo dependiente sin su rama madre satisfecha no imprime: un draft
    // con "dve_altura" suelto (rama desmarcada después) no deja basura.
    if (!cumpleDependencia(def.dependeDe, campos)) continue;
    const v = campos[def.k];
    const presente = Array.isArray(v) ? v.length > 0 : hayValor(v);
    if (!presente) continue;
    // Solo valores del catálogo del campo (mismo filtro que los hallazgos:
    // un draft con opción retirada no imprime basura).
    if (def.tipo === 'num' && !/^-?\d+(?:[.,]\d+)?$/.test(String(v).trim())) continue;
    if (def.tipo === 'select' && !def.opciones.includes(v)) continue;
    if (def.tipo === 'multi' && Array.isArray(v)) {
      const validas = v.filter((x) => def.opciones.includes(x));
      if (!validas.length) continue;
      partes.push(campoExamenTexto(def, validas));
      continue;
    }
    const linea = campoExamenTexto(def, v);
    if (linea) partes.push(linea);
  }
  return partes.length ? `${partes.join('; ')}.` : '';
}

// Escalas baseline calculadas desde el examen: solo las que computan score
// (una escala a medio llenar no imprime — igual que la evolución).
function escalasExamenTexto(ex) {
  const escalas = escalasExamenState(ex);
  const partes = [];
  for (const [id, vals] of Object.entries(escalas)) {
    const def = ESCALAS_DEF[id];
    if (!def) continue; // defensa: escala inexistente en un draft viejo
    const res = scoreEscala(id, vals);
    if (res?.texto) partes.push(res.texto);
  }
  return partes.length ? `${esExamenSuelto(ex) ? 'Escalas' : 'Escalas al recibir'}: ${partes.join('; ')}.` : '';
}

// La línea de UN segmento tal como la imprime el documento — MISMA función
// para el build y para el preview "Así queda" del editor (invariante: preview
// y documento no pueden divergir; no reintroducir textos paralelos).
// Devuelve '' si el segmento no tiene contenido.
export function lineaSegmentoExamen(ex, segId, { incluirNotas = true } = {}) {
  const e = ex || estadoInicialExamen();
  const seg = SEGMENTOS_EXAMEN.find((s) => s.id === segId);
  if (!seg || !segmentoConContenido(e, seg)) return '';
  const { sinHallazgos, hallazgos, nota, campos, gruposNormales } = segmentoExamenState(e, seg.id);
  const detalleCampos = camposSegmentoTexto(seg.id, campos);
  const grupos = GRUPOS_SEGMENTO[seg.id];
  const normalesGrupo = NORMAL_GRUPO[seg.id] || {};
  const partes = [];

  if (grupos && !sinHallazgos && (gruposNormales.length || hallazgos.length)) {
    // Composición POR PARTE (dictado 2026-07-18): cada grupo aporta sus chips
    // afirmados o, si se marcó normal, su fragmento basal — en orden de examen.
    // "En cada parte se pide si es normocráneo, y si no, describir."
    const piezas = [];
    for (const g of grupos) {
      const chipsG = seg.hallazgos.filter((h) => h.grupo === g.id && hallazgos.includes(h.v)).map((h) => lowerFirst(etiquetaHallazgoExamen(h.v, e)));
      if (chipsG.length) piezas.push(chipsG.join(', '));
      else if (gruposNormales.includes(g.id) && normalesGrupo[g.id]) piezas.push(normalesGrupo[g.id]);
    }
    if (piezas.length) partes.push(`${piezas.join(', ')}.`);
  } else {
    const validos = seg.hallazgos.map((h) => h.v).filter((v) => hallazgos.includes(v));
    if (validos.length) {
      partes.push(`${validos.map((v) => lowerFirst(etiquetaHallazgoExamen(v, e))).join(', ')}.`);
    } else if (sinHallazgos && !nota.trim() && !detalleCampos) {
      // El detalle manda: la basal completa solo se imprime si no hay nada
      // más. "Sin hallazgos" (segmento entero) firma la redacción curada.
      partes.push(`${NORMAL_SEGMENTO[seg.id] || 'sin hallazgos'}.`);
    }
  }
  if (detalleCampos) partes.push(detalleCampos);
  if (nota.trim()) {
    // 'seguras': la nota entra solo sin PII dura (nombre/RUT la dejan fuera
    // de ESTE documento; en el documento del examen sigue viva tal cual).
    const notaEntra = incluirNotas === true
      || (incluirNotas === 'seguras' && !detectPiiDuro(nota).hit);
    if (notaEntra) partes.push(nota.trim());
  }
  return `${seg.label}: ${partes.join(' ') || 'evaluado.'}`;
}

// La línea de soportes al recibir, en prosa de evolución ("Sedado con propofol
// y fentanilo; en CNAF; con noradrenalina."). LA MISMA función alimenta el
// preview de la UI y el documento (no reintroducir textos paralelos). null si
// no se constató ningún soporte.
export function lineaSoportes(ex) {
  const s = soportesExamenState(ex);
  const partes = [];
  // La droga con su dosis al lado ("propofol (30 ml/h)"); la preparación,
  // opcional, en la misma cuña ("noradrenalina 0,1 mcg/kg/min, 8 mg en 100
  // ml SG 5%"). Las dosis huérfanas de drogas des-marcadas no se imprimen.
  const conDosis = (drogas, dosisMap, prepMap) => joinProsaLista(drogas.map((d) => {
    const dosis = typeof dosisMap?.[d] === 'string' ? dosisConUnidad(d, dosisMap[d]) : '';
    const prep = typeof prepMap?.[d] === 'string' ? prepMap[d].trim() : '';
    const extra = [dosis, prep].filter(Boolean).join(', ');
    return extra ? `${lowerFirst(d)} (${extra})` : lowerFirst(d);
  }));
  if (s.sedacion === 'Con sedación') {
    const drogas = Array.isArray(s.sedoanalgesia) ? s.sedoanalgesia : [];
    let sedado = drogas.length ? `sedado con ${conDosis(drogas, s.sedoDosis)}` : 'sedado';
    // BIS: el valor del monitor cuando existe (número; nada que preguntar si no).
    if (String(s.bis ?? '').trim() !== '') sedado += `, BIS ${String(s.bis).trim()}`;
    partes.push(sedado);
    const bnm = Array.isArray(s.bnm) ? s.bnm : [];
    if (bnm.length) {
      let bloqueo = `en bloqueo neuromuscular con ${joinProsaLista(bnm.map(lowerFirst))}`;
      if (s.tof) bloqueo += `, TOF ${s.tof}`;
      partes.push(bloqueo);
    }
  } else if (s.sedacion === 'Sin sedación') {
    partes.push('sin sedación');
  }
  // Dolor: mismas palabras que la rama ("dolor no evaluable" viene de su
  // prose); el manejo es la voz del autor y va tal cual.
  if (s.dolor === 'Con dolor') {
    const manejo = typeof s.dolorManejo === 'string' ? s.dolorManejo.trim() : '';
    partes.push(manejo ? `con dolor, manejado con ${lowerFirst(manejo)}` : 'con dolor');
  } else if (s.dolor === 'Sin dolor') {
    partes.push('sin dolor');
  } else if (s.dolor === 'No evaluable') {
    partes.push('dolor no evaluable');
  }
  if (s.respiratorio) {
    // Mismas palabras que la prosa de la rama de ventilación (viaArtificial
    // ya imprime "con tubo orotraqueal"/"con traqueostomía"). El detalle
    // breve por soporte (dictado 10:48) se encadena a su base.
    const como = {
      'Aire ambiente': 'en aire ambiente', 'Oxigenoterapia': 'en oxigenoterapia',
      'CNAF': 'en CNAF', 'VNI': 'en VNI',
      'TOT': 'con tubo orotraqueal', 'TQT': 'con traqueostomía',
    };
    const num = (v) => (String(v ?? '').trim() !== '' ? String(v).trim() : null);
    let base = como[s.respiratorio] || `en ${lowerFirst(s.respiratorio)}`;
    if (s.respiratorio === 'Oxigenoterapia') {
      if (s.o2Interfaz) base += ` por ${lowerFirst(s.o2Interfaz)}`;
      if (num(s.o2Flujo)) base += ` a ${num(s.o2Flujo)} L/min`;
    } else if (s.respiratorio === 'CNAF') {
      if (num(s.cnafFlujo)) base += `, flujo ${num(s.cnafFlujo)} L/min`;
      if (num(s.cnafFio2)) base += `, FiO₂ ${num(s.cnafFio2)}%`;
    } else if (s.respiratorio === 'VNI') {
      if (s.vniModo) base += ` modo ${s.vniModo}`;
      if (num(s.vniIpap) && num(s.vniEpap)) base += `, IPAP ${num(s.vniIpap)} / EPAP ${num(s.vniEpap)} cmH₂O`;
    } else if (s.respiratorio === 'TOT' || s.respiratorio === 'TQT') {
      if (s.vmiBreve) base += `, en ${lowerFirst(s.vmiBreve)}`;
      if (num(s.vmiFio2)) base += `, FiO₂ ${num(s.vmiFio2)}%`;
      if (num(s.vmiPeep)) base += `, PEEP ${num(s.vmiPeep)} cmH₂O`;
    }
    partes.push(base);
  }
  // Hemodinamia general del chequeo (dictado 2026-08-09): estable/inestable,
  // con los porqués de catálogo cuando corresponde. "Otro" no tiene prosa
  // propia (catálogo cerrado, cero texto libre): un inestable con solo "Otro"
  // marcado, o con ids que no calzan con el catálogo, imprime la frase base.
  if (s.hemoEstado === 'Estable') {
    partes.push('hemodinámicamente estable');
  } else if (s.hemoEstado === 'Inestable') {
    const porques = Array.isArray(s.hemoInestablePorque) ? s.hemoInestablePorque : [];
    const prosas = porques
      .map((id) => HEMO_INESTABLE_PORQUE.find((p) => p.id === id))
      .filter((p) => p && p.prosa)
      .map((p) => p.prosa);
    partes.push(prosas.length
      ? `hemodinámicamente inestable por ${joinProsaLista(prosas)}`
      : 'hemodinámicamente inestable');
  }
  if (s.dvaSoporte === 'Con drogas vasoactivas') {
    const dva = Array.isArray(s.dva) ? s.dva : [];
    partes.push(dva.length ? `con ${conDosis(dva, s.dvaDosis, s.dvaPreparacion)}` : 'con drogas vasoactivas');
  } else if (s.dvaSoporte === 'Sin drogas vasoactivas') {
    partes.push('sin drogas vasoactivas');
  }
  if (!partes.length) return null;
  const frase = partes.join('; ');
  return `${frase.charAt(0).toUpperCase()}${frase.slice(1)}.`;
}

export function buildExamenFisico(ex, { incluirTitulo = true, incluirNotas = true } = {}) {
  const e = ex || estadoInicialExamen();
  const lineas = [];

  // Apertura como oración completa ("Paciente en regular estado general,
  // somnoliento, en decúbito supino.") — no fragmentos sueltos.
  const posicion = e.posicion ? lowerFirst(e.posicion) : null;
  const apertura = [
    e.impresion ? `en ${lowerFirst(e.impresion)}` : null,
    e.conciencia ? lowerFirst(e.conciencia) : null,
    posicion ? (/^decúbito/.test(posicion) ? `en ${posicion}` : posicion) : null,
  ].filter(Boolean).join(', ');
  if (apertura) lineas.push(`Paciente ${apertura}.`);

  // Los soportes al recibir van justo tras la apertura: son parte de la foto
  // ("cómo lo recibí"), antes de los signos y del recorrido céfalo-caudal.
  const soportes = lineaSoportes(e);
  if (soportes) lineas.push(soportes);

  const signos = signosTexto(e);
  if (signos) lineas.push(signos);
  const abcde = abcdeTexto(e);
  if (abcde) lineas.push(abcde);

  for (const seg of SEGMENTOS_EXAMEN) {
    if (!segmentoConContenido(e, seg)) continue;
    lineas.push(lineaSegmentoExamen(e, seg.id, { incluirNotas }));
  }

  // Piel y puntos de apoyo (mapa corporal): la foto de piel, los controles con
  // hora y los hallazgos del turno. Catálogo cerrado — entra también a la
  // variante PII-safe (examenParaPae) sin filtro.
  for (const lp of lineasPiel(e)) lineas.push(lp);

  const escalas = escalasExamenTexto(e);
  if (escalas) lineas.push(escalas);

  if (!lineas.length) return { lineas: [], texto: '' };

  // El examen suelto no imprime la hora en el título (norma del fundador del
  // 2026-08-02: el contexto ancla, la hora vive en los datos). En el turno el
  // título conserva su hora de siempre.
  const titulo = `EXAMEN FÍSICO${e.hora && !esExamenSuelto(e) ? ` · ${e.hora}` : ''}`;
  const texto = incluirTitulo ? [titulo, ...lineas].join('\n') : lineas.join('\n');
  return { lineas, texto };
}

// ── La foto entra al PAE (D-2, aprobada 2026-07-09) ──────────────────────────
// Bloque de UNA línea para `paePrefillDesdeTurno`: hora + apertura + signos +
// ABCDE + hallazgos y campos por segmento + escalas baseline. PII-safe por
// construcción: todo es catálogo cerrado o número — la nota libre JAMÁS viaja
// (misma garantía dura del puente examen→evolución). '' si no hay examen.
// Segmentos cuya nota libre quedó FUERA del documento de la visita por la
// guarda dura de PII (incluirNotas: 'seguras'). Para el AVISO de la UI:
// la doctrina del repo dice que ningún descarte muere mudo — sin esto, la
// exclusión repetía en chico la amputación silenciosa del incidente
// "¡los brazos!" para las notas que tropiezan con la guarda (incluidos sus
// falsos positivos, que el aviso convierte en corregibles). Devuelve labels
// de catálogo, jamás el texto de la nota.
// Lo que trae un dato DURO del paciente en el texto libre del examen, con los
// fragmentos detectados, en la forma que pide `PulirEvolucion` (`bloqueoPii`):
// las notas de cada segmento y los signos (sus campos son de texto y la línea
// los imprime tal cual vienen). Mismo patrón que `sistemasConNotaPii` de la
// evolución: con alguno, el examen NO viaja a la IA al pulir (2026-10-05: el
// pulido del examen no tenía esta guarda y la pieza suelta lo abre a más gente).
export function segmentosConNotaPii(ex) {
  const e = ex || estadoInicialExamen();
  const porSegmento = SEGMENTOS_EXAMEN
    .map((seg) => {
      const { nota } = segmentoExamenState(e, seg.id);
      const { hit, matches } = detectPiiDuro(nota || '');
      return hit ? { id: seg.id, label: seg.label, valores: matches.map((m) => m.valor) } : null;
    })
    .filter(Boolean);
  const signos = Object.values(signosExamenState(e)).filter((v) => hayValor(v)).map(String).join(' · ');
  const enSignos = detectPiiDuro(signos);
  return enSignos.hit
    ? [...porSegmento, { id: 'signos', label: 'Signos', valores: enSignos.matches.map((m) => m.valor) }]
    : porSegmento;
}

export function notasExcluidasPorPii(ex) {
  const e = ex || estadoInicialExamen();
  return SEGMENTOS_EXAMEN
    .filter((seg) => {
      const { nota } = segmentoExamenState(e, seg.id);
      return nota.trim() && detectPiiDuro(nota).hit;
    })
    .map((seg) => seg.label);
}

export function examenParaPae(ex) {
  if (!examenTieneContenido(ex)) return '';
  const { lineas } = buildExamenFisico(ex, { incluirTitulo: false, incluirNotas: false });
  if (!lineas.length) return '';
  // Sin hora impresa (norma del fundador, 2026-08-02: en beta el documento
  // ancla con contexto, no con hora exacta; hora y ts siguen en los datos) y
  // sin guion largo (es texto generado que aterriza en la anamnesis del PAE).
  return `Examen físico: ${lineas.join(' ')}`;
}

// ── Escalas sugeridas desde el examen (D-5, set completo) ─────────────────────
// El examen OFRECE la escala en el momento clínico correcto y la calcula con
// el motor existente (mismos ítems y cortes): Braden al recibir (el momento en
// que se calcula — ancla LPP), Glasgow si la conciencia se constató, RASS al
// sedado o agitado y FOUR si llegó comprometida sin sedación (objetivar el
// baseline), Downton si hay
// movilidad disminuida (riesgo de caídas). Devuelve ids por contexto para que la
// UI las cuelgue donde tocan.
export function escalasSugeridasExamen(ex) {
  const e = ex || estadoInicialExamen();
  const sugeridas = { conciencia: [], movilidad: [], dolor: [], sacra: ['braden'], genitales: [] };
  const sop = soportesExamenState(e);
  // Glasgow/RASS SIEMPRE que la conciencia se constató (dictado del fundador,
  // 2026-07-27): la conservada también se objetiva ("Glasgow 15/15" documenta
  // la basal de la mañana y habilita el delta contra el examen de control).
  // SEDADO (v2 2026-08-01): la profundidad se objetiva con RASS; el Glasgow
  // bajo sedación se invalida (misma regla que la rama de la evolución).
  // FOUR (dictado del fundador en turno real, 2026-08-20: "paciente con TQT
  // que no responde, sin sedación"): el paciente de BAJA RESPUESTA SIN
  // SEDACIÓN se objetiva con FOUR (desde 2026-09-29 ya no al sedado: "el FOUR
  // es en no sedados que no pueden comunicarse"). Es la MISMA regla que la
  // rama neuro de la evolución (ESCALAS_POR_SISTEMA) —
  // la visita se había quedado atrás y ofrecía solo Glasgow, justo la escala
  // que la vía aérea artificial invalida en su componente verbal; FOUR es la
  // que sigue siendo válida en el intubado/traqueostomizado. La "conciencia
  // comprometida" de ESTA pantalla ya está definida en CONCIENCIA_RED (sopor /
  // no responde, la misma bandera que despliega la D del ABCDE): cero catálogo
  // paralelo.
  //
  // Solo lo que AMERITA (dictado del fundador, 2026-09-29: "si el paciente
  // está vigil, Glasgow 15, no necesitamos RASS"; misma regla que el `amerita`
  // de la evolución): RASS mide sedación y agitación, así que se ofrece al
  // sedado y al agitado, no al vigil ni al que está en sopor (ahí mandan
  // Glasgow y FOUR).
  const comprometida = CONCIENCIA_COMPROMETIDA.includes(e.conciencia);
  const sedado = sop.sedacion === 'Con sedación';
  if (sedado) {
    sugeridas.conciencia = ['rass'];
  } else if (e.conciencia) {
    sugeridas.conciencia = comprometida
      ? ['glasgow', 'four']
      : e.conciencia === 'Agitación psicomotora' ? ['glasgow', 'rass'] : ['glasgow'];
  }
  // Dolor declarado → se objetiva (regla 2026-07-14: EVA 0 documenta la
  // ausencia; CPOT para el no comunicante). EXCEPTO "No evaluable" (dictado
  // del fundador 2026-08-10, probando el preview): declarar que el dolor no
  // se puede objetivar AHORA es en sí el dato, y ofrecer EVA/CPOT encima se
  // contradice — las escalas no se muestran.
  // Desde 2026-09-29 se ofrece LA que corresponde: EVA a quien puede contar
  // su dolor, CPOT al sedado o en sopor, y las dos al agitado (puede o no
  // poder contarlo). Esta pantalla no pregunta el objetivo de sedación, así
  // que el sedado recibe CPOT; la EVA de la sedación ligera vive en la Mesa.
  if (sop.dolor && sop.dolor !== 'No evaluable') {
    sugeridas.dolor = sedado || comprometida
      ? ['cpot']
      : e.conciencia === 'Agitación psicomotora' ? ['eva', 'cpot'] : ['eva'];
  }
  const movilidadMarcada = ['eess', 'eeii'].some((id) =>
    segmentoExamenState(e, id).hallazgos.includes('Movilidad disminuida'));
  // Solo Downton (2026-09-29): Morse se podó de la evolución el 2026-07-06
  // porque Downton es el estándar de MiPAE, y la visita seguía ofreciendo las
  // dos para el mismo riesgo. Un Morse ya puntuado se sigue dibujando
  // (ESCALAS_POSIBLES_EXAMEN) y sigue en el documento del examen.
  if (movilidadMarcada) sugeridas.movilidad = ['downton'];
  // DAI constatada → su severidad se objetiva con la escala Ghent (pedido del
  // fundador 2026-08-09: "al apretar DAI no abre ninguna escala"). Misma
  // definición que la evolución y que la lesión del mapa: fuente única
  // ESCALAS_DEF.dai, cero catálogo paralelo. Cuenta como DAI el hallazgo del
  // grupo o una localización ya marcada.
  const gen = segmentoExamenState(e, 'genitales');
  const daiMarcada = gen.hallazgos.includes('Eritema perineal (DAI)')
    || gen.hallazgos.includes('Piel macerada por humedad')
    || (Array.isArray(gen.campos?.dai_localizacion) && gen.campos.dai_localizacion.length > 0);
  if (daiMarcada) sugeridas.genitales = ['dai'];
  return sugeridas;
}

// Las escalas de cada contexto que la pantalla DIBUJA: las sugeridas MÁS las
// que ya tienen valor. Un dato registrado siempre tiene que ser alcanzable
// para verlo, corregirlo o borrarlo (mismo contrato que escalasDibujables de
// la evolución): si la autora puntuó RASS y después marcó "Vigil y conectado",
// el RASS ya no se ofrece, pero sigue en el documento y tiene que poder verse.
// El orden es el del catálogo de cada contexto.
const ESCALAS_POSIBLES_EXAMEN = {
  conciencia: ['glasgow', 'rass', 'four'],
  dolor: ['eva', 'cpot'],
  movilidad: ['morse', 'downton'],
  sacra: ['braden'],
  genitales: ['dai'],
};
export function escalasDibujablesExamen(ex) {
  const sugeridas = escalasSugeridasExamen(ex);
  const escalas = escalasExamenState(ex);
  const out = {};
  for (const [ctx, posibles] of Object.entries(ESCALAS_POSIBLES_EXAMEN)) {
    const sug = new Set(sugeridas[ctx] || []);
    out[ctx] = posibles.filter((id) => sug.has(id) || escalaConValores(id, escalas[id]));
  }
  return out;
}

// ── Puente examen → evolución ─────────────────────────────────────────────────
// Solo los hallazgos ETIQUETADOS del catálogo viajan (nunca la nota libre:
// garantía dura de que al puente no entra texto libre de pacientes). Cada
// hallazgo enrutado queda anotado en `evolucion._examenEnrutado` (mismo patrón
// idempotente que `_accionesEnrutadas` del puente turno → evolución): traerlo
// dos veces no duplica.
const claveHallazgo = (segId, v) => `${segId}:${v}`;

// Hallazgos etiquetados aún no enrutados, agrupados por sistema destino.
export function hallazgosEnrutables(ex, evolucion) {
  const yaEnrutados = new Set(evolucion?._examenEnrutado || []);
  const porSistema = {};
  for (const seg of SEGMENTOS_EXAMEN) {
    const { hallazgos } = segmentoExamenState(ex, seg.id);
    for (const h of seg.hallazgos) {
      if (!h.evolucion || !hallazgos.includes(h.v)) continue;
      if (yaEnrutados.has(claveHallazgo(seg.id, h.v))) continue;
      if (!SISTEMAS_EVOLUCION.some((s) => s.id === h.evolucion)) continue; // defensa: sistema inexistente
      const lista = porSistema[h.evolucion] || (porSistema[h.evolucion] = []);
      lista.push({ segmento: seg, hallazgo: h.v });
    }
  }
  return porSistema;
}

export function nHallazgosEnrutables(ex, evolucion) {
  return Object.values(hallazgosEnrutables(ex, evolucion)).reduce((n, l) => n + l.length, 0);
}

// Vuelca los hallazgos enrutables como línea de nota en el sistema destino
// ("Al examen físico: eritema que no blanquea (zona sacra y puntos
// de apoyo)."). Agrega, nunca pisa (misma política que mergeEvolucion). Pura.
// Devuelve { evolucion, agregadas: n }.
// Los campos medidos no son hallazgos rápidos. Su proyección se recalcula
// desde cada foto, con su contexto temporal, sin mezclar la nota editable.
const DESTINO_NOTA_EXAMEN = {
  cabeza: 'neuro', cuello: 'hemodinamia', torax: 'ventilacion', abdomen: 'gastro',
  eess: 'hemodinamia', eeii: 'hemodinamia', genitales: 'tegumentario',
  espalda: 'tegumentario', sacra: 'tegumentario',
};

// Un segmento contiene distintos sistemas: piel abdominal no es digestivo,
// ni piel de una extremidad es perfusión. Se reconocen
// cláusulas explícitas, conservando sus palabras, lateralidad y negaciones.
// Una cláusula mixta o no reconocida permanece íntegra en su sitio habitual;
// no se adivina qué parte modifica un adjetivo ni se fabrican campos clínicos.
const normalizarNotaExamen = texto => texto.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
const DISPOSITIVO_EN_NOTA = /\b(?:drenajes?|sondas?|cateter\w*|cvc|picc|tot|tqt|traqueostom\w*|tubo\s+(?:endotraqueal|pleural))\b/;

function sistemasExplicitosNota(n) {
  const candidatos = [];
  if (/\b(?:paresia|plejia|hemiparesia|hemiplejia|tetraparesia|tetraplejia|paraplejia|movilidad|movil(?:es)?|mueve\w*|motor\w*|fuerza|sensibilidad|contenid[oa]s?|contenci(?:on|ones)|pupilas?|glasgow)\b/.test(n)) candidatos.push('neuro');
  // El plural cambia z por c. La transposición «ciactrices» solo se
  // reconoce con contexto de antigüedad o quirúrgico; el literal no se corrige.
  if (/\b(?:ho|heridas?|sutur\w*|cicatriz\w*|cicatrices|lpp|ulceras?|eritem\w*|enrojec\w*|flicten\w*|equimosis|aposito\w*)\b/.test(n)
    || /\bciactrices\s+(?:quirurgicas|antiguas)\b/.test(n)
    // El tema explícito alcanza: una descripción cutánea libre no necesita
    // coincidir con un adjetivo del catálogo para conservar su sistema.
    // Si también habla de perfusión o movilidad sigue siendo cláusula mixta.
    || /\b(?:piel|cutane[oa]s?|dermatitis|rash|exantema\w*)\b/.test(n)
    || (/\bpliegues?\b/.test(n) && /\b(?:resec[oa]s?|sec[oa]s?|sequedad|resequedad)\b/.test(n))
    || /\btalon(?:es)?\b.*\b(?:lesion\w*|blanque\w*|indemne|reseca)\b/.test(n)) candidatos.push('tegumentario');
  if (/\b(?:cni|compresion neumatica intermitente|tromboprofilaxis)\b/.test(n)) candidatos.push('hemato');
  if (/\b(?:perfusion|perfundid\w*|pulsos?|llene capilar|edemas?|tibi[oa]s?|frialdad)\b/.test(n)) candidatos.push('hemodinamia');
  if (/\b(?:depresible|distension|ruidos hidroaereos|rha|peristalt\w*|deposicion\w*|vomit\w*|nausea\w*)\b|\babdomen\s+(?:blando|indoloro|distendido)\b/.test(n)) candidatos.push('gastro');
  if (/\b(?:murmullo pulmonar|ruidos respiratorios|crepitos|sibilancias|roncus|disnea|eupneic\w*|expansion toracica)\b/.test(n)) candidatos.push('ventilacion');
  return candidatos;
}

// Una coma también une adjetivos o una negación compartida. Solo abre una
// segunda cláusula cuando el siguiente sujeto tiene un sitio propio explícito
// y ambas partes nombran un único sistema distinto, sin perder modificadores.
function separarClausulasNota(clausula) {
  const n = normalizarNotaExamen(clausula);
  if (/\b(?:sin|no|ni|ausen\w*|nieg\w*|negad\w*|descart\w*|vigila\w*|evalua\w*|valora\w*|sospech\w*|posibles?|riesgo|buscar|observacion|pendiente\w*|preven\w*|evitar)\b/.test(n)
    || DISPOSITIVO_EN_NOTA.test(n)) return [clausula];
  const partes = clausula.split(/,\s+/);
  if (partes.length !== 2) return [clausula];
  const [izquierda, derecha] = partes.map(normalizarNotaExamen);
  const sistemas = partes.map(p => sistemasExplicitosNota(normalizarNotaExamen(p)));
  const sujetoDeclarado = /^(?:piel|superficie cutanea|(?:cambios|marcas) cutane[oa]s?|equimosis|cicatrices|dermatitis|rash|exantema\w*|edemas?|pulsos|abdomen|murmullo pulmonar)\b/;
  const sujetoPropio = /^(?:edemas?\s+en\s+(?:(?:ambas|ambos|las|los|la|el)\s+)?(?:eeii|eess|piernas?|brazos?|manos?|pies)|pulsos\s+(?:radiales|pedios|tibiales|femorales)|abdomen\s+(?:blando|indoloro|distendido)|murmullo\s+pulmonar|equimosis\s+en\s+)/;
  if (!sujetoDeclarado.test(izquierda) || !sujetoPropio.test(derecha) || sistemas.some(s => s.length !== 1)
    || sistemas[0][0] === sistemas[1][0]) return [clausula];
  return partes;
}

function notasExamenPorSistema(segmento, nota) {
  const texto = String(nota || '').trim();
  if (!texto || detectPii(texto).hit || detectPiiDuro(texto).hit) return {};
  const habitual = DESTINO_NOTA_EXAMEN[segmento];
  const extremidad = ['eess', 'eeii'].includes(segmento);
  const out = {};
  // No parte decimales: el punto solo separa cuando lo sigue un espacio.
  const clausulas = texto.split(/\n+|;\s*|(?<=[.!?])\s+/).map(t => t.trim()).filter(Boolean).flatMap(separarClausulasNota);
  let contextoPiel = false;
  let contextoDispositivo = false;
  for (const clausula of clausulas) {
    const n = normalizarNotaExamen(clausula);
    const candidatos = sistemasExplicitosNota(n);
    const dispositivo = DISPOSITIVO_EN_NOTA.test(n);
    // La curación genérica no identifica un sistema: solo continúa piel si
    // la oración previa lo documentó y no apareció otro sistema/dispositivo.
    const cuidadoPiel = contextoPiel && candidatos.length === 0
      && /\b(?:curacion\w*|colchon\w*|cambios? posturales?|proteccion\s+(?:de\s+)?zonas?\s+de\s+apoyo)\b/.test(n);
    const unico = candidatos.length === 1 ? candidatos[0] : null;
    // El apósito inmediato de un drenaje no cambia a piel por estar en otra
    // oración. Una nueva localización o hallazgo cutáneo corta ese contexto.
    const cuidadoDispositivo = contextoDispositivo && (!candidatos.length || unico === 'tegumentario')
      && /\b(?:aposito\w*|curacion\w*)\b/.test(n)
      && !/\b(?:piel|cutane[oa]s?|dermatitis|rash|exantema\w*|heridas?|ho|lpp|ulceras?|equimosis|eritem\w*|talon\w*|codos?|piernas?|brazos?|manos?|pies|sacr\w*|glute\w*)\b/.test(n);
    const enrutable = unico === 'tegumentario'
      || (extremidad && ['neuro', 'hemodinamia', 'hemato'].includes(unico));
    const sistema = dispositivo || cuidadoDispositivo ? habitual
      : cuidadoPiel ? 'tegumentario'
        : enrutable ? unico : habitual;
    if (sistema) (out[sistema] ||= []).push(clausula.replace(/\.$/, ''));
    contextoPiel = !dispositivo && !cuidadoDispositivo && sistema === 'tegumentario' && (unico === 'tegumentario' || cuidadoPiel);
    contextoDispositivo = dispositivo || cuidadoDispositivo;
  }
  // Si no hubo piel que separar, la proyección histórica de los otros
  // segmentos conserva incluso sus puntos y saltos originales.
  if (!extremidad && Object.keys(out).length === 1 && out[habitual]) return { [habitual]: [texto.replace(/\.$/, '')] };
  return out;
}

export function proyectarCamposExamen(ex, evolucion) {
  const base = evolucion || { sistemas: {} };
  const proyeccion = {};
  const conciencia = [];
  const destino = (seg, k) => {
    if (/^(pupilas_|dve_|pic$|ppc$|fuerza$)/.test(k)) return 'neuro';
    if (/^(tot_|tqt_)/.test(k) || seg === 'torax') return 'ventilacion';
    if (/^(pulsos$|llene_capilar_seg$)/.test(k)) return 'hemodinamia';
    if (seg === 'abdomen') return 'gastro';
    if (seg === 'genitales') return 'tegumentario';
    return null;
  };
  for (const [foto, contexto] of [[ex, 'al recibir'], [ex?.control, 'de control']]) {
    if (!foto) continue;
    if (CONCIENCIA.includes(foto.conciencia)) conciencia.push({ valor: foto.conciencia, contexto });
    const porFoto = {};
    for (const seg of SEGMENTOS_EXAMEN) {
      const estado = segmentoExamenState(foto, seg.id);
      const campos = estado.campos;
      const notas = notasExamenPorSistema(seg.id, estado.nota);
      for (const sis of SISTEMAS_EVOLUCION) {
        const keys = (CAMPOS_EXAMEN[seg.id] || []).filter((d) => destino(seg.id, d.k) === sis.id).map((d) => d.k);
        const texto = camposSegmentoTexto(seg.id, campos, keys);
        const hallazgos = seg.hallazgos.filter(h => estado.hallazgos.includes(h.v)
          && (h.evolucion || (seg.id === 'genitales' ? 'tegumentario' : null)) === sis.id)
          .map(h => lowerFirst(h.v));
        const partes = [...hallazgos, ...(texto ? [texto.replace(/\.$/, '')] : [])];
        // La normalidad abdominal firmada también es un hallazgo documentado.
        if (seg.id === 'abdomen' && sis.id === 'gastro' && estado.sinHallazgos
          && !estado.hallazgos.length && !texto && !estado.nota.trim()) partes.push(NORMAL_SEGMENTO.abdomen);
        // La descripción libre conserva sitio y palabras del autor, sin
        // convertirla en diagnóstico. Solo proyección documental, sin sembrar
        // campos clínicos, y excluida si contiene PII.
        partes.push(...(notas[sis.id] || []));
        if (partes.length) (porFoto[sis.id] ||= []).push(`${seg.label}: ${partes.join('; ')}`);
      }
    }
    const hemo = soportesExamenState(foto);
    if (HEMO_ESTADO.includes(hemo.hemoEstado)) {
      const motivos = (Array.isArray(hemo.hemoInestablePorque) ? hemo.hemoInestablePorque : [])
        .map(id => HEMO_INESTABLE_PORQUE.find(p => p.id === id)?.prosa).filter(Boolean);
      (porFoto.hemodinamia ||= []).unshift(`Hemodinámicamente ${lowerFirst(hemo.hemoEstado)}${hemo.hemoEstado === 'Inestable' && motivos.length ? ' por ' + joinProsaLista(motivos) : ''}`);
    }
    if (POSICIONES.includes(foto.posicion)) (porFoto.ventilacion ||= []).unshift(`Posición: ${lowerFirst(foto.posicion)}.`);
    for (const [sistema, partes] of Object.entries(porFoto)) {
      (proyeccion[sistema] ||= []).push(`Al examen físico ${contexto}: ${partes.map(p => p.replace(/\.$/, '')).join('; ')}.`);
    }
  }
  const sistemas = { ...(base.sistemas || {}) };
  let cambio = false;
  for (const sis of SISTEMAS_EVOLUCION) {
    const texto = proyeccion[sis.id]?.join('\n\n') || '';
    const concienciaCambio = sis.id === 'neuro'
      && JSON.stringify(sistemas.neuro?._concienciaExamen || []) !== JSON.stringify(conciencia);
    // Solo las líneas del catálogo que ya tienen portador en la proyección
    // pueden omitirse al imprimir. La nota original y las ediciones sobreviven.
    const permitidas = new Set();
    for (const foto of [ex, ex?.control].filter(Boolean)) {
      for (const seg of SEGMENTOS_EXAMEN) for (const h of seg.hallazgos) {
        if (h.evolucion !== sis.id || !segmentoExamenState(foto, seg.id).hallazgos.includes(h.v)) continue;
        const item = `${lowerFirst(h.v)} (${lowerFirst(seg.label)})`;
        permitidas.add(base.sexo === 'F' ? generizarFemenino(item) : item);
      }
    }
    const omitidas = String(sistemas[sis.id]?.nota || '').split('\n').flatMap(l => {
      const m = l.trim().match(/^Al examen físico: (.+?)\.(?=\s|$)/);
      return m && m[1].split('; ').every(item => permitidas.has(item)) ? [m[0]] : [];
    });
    if ((sistemas[sis.id]?._examenCampos || '') === texto
      && JSON.stringify(sistemas[sis.id]?._examenLineasProyectadas || []) === JSON.stringify(omitidas)
      && !concienciaCambio) continue;
    sistemas[sis.id] = { ...sistemas[sis.id], _examenCampos: texto,
      _examenLineasProyectadas: omitidas,
      ...(sis.id === 'neuro' && (conciencia.length || sistemas.neuro?._concienciaExamen) ? { _concienciaExamen: conciencia } : {}),
      ...(texto || (sis.id === 'neuro' && conciencia.length) ? { sinHallazgos: false } : {}) };
    cambio = true;
  }
  return cambio ? { ...base, sistemas } : base;
}

export function mergeExamenEnEvolucion(ex, evolucion, origen = ex) {
  evolucion = proyectarCamposExamen(origen, evolucion);
  const base = evolucion && typeof evolucion === 'object' ? evolucion : { sistemas: {} };
  const porSistema = hallazgosEnrutables(ex, base);
  const entradas = Object.entries(porSistema);
  if (!entradas.length) return { evolucion: base, agregadas: 0 };

  // "Al examen físico" ES el contexto; la hora no se imprime (norma del
  // fundador, 2026-08-02): en beta la evolución no necesita la hora exacta
  // del examen. El sello vive en los datos (ex.hora / ex.ts), no en la prosa.
  const sistemas = { ...(base.sistemas || {}) };
  const claves = [...(base._examenEnrutado || [])];
  let agregadas = 0;
  // Concordancia de género de la frase que ESCRIBE MiPAE (bug de la auditoría
  // del 30-jul). El puente deja su prosa dentro de `nota`, y buildEvolucion NO
  // generiza ese campo A PROPÓSITO: es la voz de la autora, no texto de la app.
  // La regla es correcta; el defecto era meter prosa de la app en ese campo. El
  // catálogo del examen emite justo las palabras de la lista (somnoliento,
  // orientado, eupneico, normocéfalo…), así que una paciente mujer recibía la
  // forma masculina en su propia evolución. Se generiza acá, al escribir.
  // El arreglo de fondo es que la prosa de la app deje de compartir campo con la
  // de la usuaria; va en el rediseño (ver analisis/trayectoria-del-turno.md).
  const generiza = (t) => (base?.sexo === 'F' ? generizarFemenino(t) : t);

  for (const [sisId, lista] of entradas) {
    const prev = sistemas[sisId] || { hallazgos: [], nota: '' };
    const frase = generiza(`Al examen físico: ${lista
      .map(({ segmento, hallazgo }) => `${lowerFirst(hallazgo)} (${lowerFirst(segmento.label)})`)
      .join('; ')}.`);
    const notaPrev = typeof prev.nota === 'string' ? prev.nota.trim() : '';
    sistemas[sisId] = {
      ...prev,
      nota: notaPrev ? `${notaPrev}\n${frase}` : frase,
      // El flag "sin hallazgos" del sistema queda obsoleto si llega contenido
      // real — mismo reset que aplican mergeEvolucion y los caminos manuales.
      sinHallazgos: false,
    };
    for (const { segmento, hallazgo } of lista) {
      claves.push(claveHallazgo(segmento.id, hallazgo));
      agregadas += 1;
    }
  }

  return { evolucion: proyectarCamposExamen(origen, { ...base, sistemas, _examenEnrutado: claves }), agregadas };
}

// ── Puente soportes → evolución (dictado 2026-08-01) ─────────────────────────
// Los soportes constatados al recibir SIEMBRAN los campos de las ramas de la
// evolución (neuro.sedacion/sedoanalgesia, ventilacion.viaAerea + soporte o
// viaArtificial, hemodinamia.dvaSoporte/dva): la rama abre ya respondida y la
// prosa sale del MISMO build de siempre (cero texto paralelo). Como los
// strings son idénticos por construcción (SOPORTES_EXAMEN deriva de
// CAMPOS_POR_SISTEMA), el mapeo es identidad; solo la vía aérea se reparte a
// su lado de la rama. Catálogo cerrado: al puente no entra texto libre.
// Un valor de catálogo cuenta solo si trae contenido real: null, vacío o puros
// espacios NO entran al patch (ver el porqué en `detallePorDroga`).
const soporteLleno = (v) => v !== undefined && v !== null && String(v).trim() !== '';

export function soportesParaEvolucion(ex) {
  const s = soportesExamenState(ex);
  const out = {};
  // Dosis por droga → el shape porDroga de la evolución ({droga: {dosis}}),
  // sin tendencia: la foto de recepción no tiene tendencia todavía. La
  // preparación NO viaja (la evolución no tiene ese campo; queda en la prosa
  // del examen).
  //
  // Un detalle A MEDIO ESCRIBIR no viaja (ola "El estándar Bupa", 2026-08-15).
  // Desde que los puentes corren también al cerrar cada estación de la visita,
  // el merge puede pillar la dosis de la segunda droga todavía en blanco. Si
  // ese detalle parcial entrara al patch, su clave quedaría anotada en
  // `_soportesEnrutados` y la dosis que la enfermera escribe DESPUÉS ya no
  // cruzaría nunca (la clave se marca aunque no se siembre — así se respeta el
  // borrado deliberado en la Mesa, contrato que NO cambia). Así que el detalle
  // solo viaja cuando TODAS las drogas marcadas tienen dosis: mientras falte
  // una, la clave no se marca y la corrección posterior sí llega.
  const detallePorDroga = (drogas, dosisMap) => {
    const det = {};
    for (const d of drogas) {
      const dosis = typeof dosisMap?.[d] === 'string' ? dosisMap[d].trim() : '';
      if (!dosis) return null;
      det[d] = { dosis };
    }
    return Object.keys(det).length ? det : null;
  };
  const neuro = {};
  if (soporteLleno(s.sedacion)) neuro.sedacion = s.sedacion;
  if (s.sedacion === 'Con sedación' && Array.isArray(s.sedoanalgesia) && s.sedoanalgesia.length) {
    neuro.sedoanalgesia = [...s.sedoanalgesia];
    const det = detallePorDroga(s.sedoanalgesia, s.sedoDosis);
    if (det) neuro.sedoDetalle = det;
  }
  if (s.sedacion === 'Con sedación') {
    if (String(s.bis ?? '').trim() !== '') neuro.bis = String(s.bis).trim();
    if (Array.isArray(s.bnm) && s.bnm.length) {
      neuro.bnm = [...s.bnm];
      if (soporteLleno(s.tof)) neuro.tof = s.tof;
    }
  }
  if (soporteLleno(s.dolor)) neuro.dolor = s.dolor;
  if (s.dolor === 'Con dolor' && typeof s.dolorManejo === 'string' && s.dolorManejo.trim()) {
    // El manejo del dolor es el ÚNICO texto libre de esta superficie, y desde
    // que los puentes corren solos cruza a la evolución sin que nadie lo mire:
    // pasa la guarda de PII antes de viajar (regla del proyecto). Con PII no
    // entra al patch y su clave no se marca, así que corregido el texto la
    // versión limpia sí cruza después.
    if (!detectPii(s.dolorManejo).hit) neuro.dolorManejo = s.dolorManejo.trim();
  }
  if (Object.keys(neuro).length) out.neuro = neuro;
  // El select breve mezcla los dos lados de la rama, y la frontera la marca
  // RESPIRATORIO_NATIVO. Un valor que no está en NINGUNO de los dos catálogos
  // (jsonb adulterado, o un draft de una versión con otro vocabulario) NO
  // siembra nada: el `else` a secas lo daba por artificial y la evolución
  // terminaba AFIRMANDO una vía aérea artificial que el paciente no tiene, que
  // es de los errores más graves que puede tener el documento. Misma regla que
  // Downton (#721): valor fuera de catálogo no afirma, calla.
  const respArtificial = (campoEvolucion('ventilacion', 'viaArtificial').opciones || []);
  const respConocido = soporteLleno(s.respiratorio)
    && (RESPIRATORIO_NATIVO.includes(s.respiratorio) || respArtificial.includes(s.respiratorio));
  if (respConocido) {
    out.ventilacion = RESPIRATORIO_NATIVO.includes(s.respiratorio)
      ? { viaAerea: 'Vía aérea nativa', soporte: s.respiratorio }
      : { viaAerea: 'Vía aérea artificial', viaArtificial: s.respiratorio };
    if (respArtificial.includes(s.respiratorio) && soporteLleno(s.vmiBreve)) {
      out.ventilacion.vmiModo = s.vmiBreve;
      // La rama de la evolución exige el eslabón: TQT en VMI declara tqtSoporte.
      if (s.respiratorio === 'TQT') out.ventilacion.tqtSoporte = 'En VMI';
    }
    // Detalle breve por soporte (dictado 10:48): las claves son LAS MISMAS de
    // la rama de ventilación, así que siembran por identidad. Solo viajan las
    // del soporte declarado (un flujo de CNAF huérfano no ensucia nada).
    const DETALLE_POR_SOPORTE = {
      'Oxigenoterapia': ['o2Interfaz', 'o2Flujo'],
      'CNAF': ['cnafFlujo', 'cnafFio2'],
      'VNI': ['vniModo', 'vniIpap', 'vniEpap'],
      'TOT': ['vmiFio2', 'vmiPeep'],
      'TQT': ['vmiFio2', 'vmiPeep'],
    };
    for (const k of (DETALLE_POR_SOPORTE[s.respiratorio] || [])) {
      const val = s[k];
      if (val != null && String(val).trim() !== '') out.ventilacion[k] = String(val).trim();
    }
  }
  const hemo = {};
  if (soporteLleno(s.dvaSoporte)) hemo.dvaSoporte = s.dvaSoporte;
  if (s.dvaSoporte === 'Con drogas vasoactivas' && Array.isArray(s.dva) && s.dva.length) {
    hemo.dva = [...s.dva];
    const det = detallePorDroga(s.dva, s.dvaDosis);
    if (det) hemo.dvaDetalle = det;
  }
  if (Object.keys(hemo).length) out.hemodinamia = hemo;
  return out;
}

// ¿Este valor del chequeo dice "todo normal"? La decisión vive en una sola
// función compartida (`valorNormalDeCampo`, escalasEvolucion.js): la Mesa usa
// LA MISMA para reconocer un sistema que solo trae sembrado-normal, así que el
// chip que se ofrece y el documento que se imprime no pueden discrepar.
const soporteEsNormal = (sisId, k, v) => valorNormalDeCampo(sisId, k, v);

// Siembra idempotente (mismo patrón de claves que `_examenEnrutado`): cada
// campo sembrado queda anotado en `evolucion._soportesEnrutados` y no se
// vuelve a ofrecer — y JAMÁS pisa un campo que la autora ya respondió (si el
// valor difiere, manda el de la evolución: es más nuevo que la foto).
//
// Basal FIRMADA (hallazgo H1 de la auditoría de la ola "El estándar Bupa",
// 2026-08-15): "Sin hallazgos" en la Mesa imprime la basal rica de
// NORMAL_SISTEMA y es una DECLARACIÓN de la autora — "jamás pisar lo
// respondido" la cubre igual que a un campo. Un chequeo cuyo aporte al sistema
// es solo "todo normal" no dice nada que esa basal no diga ya: sembrarlo
// volteaba `sinHallazgos` y reemplazaba la prosa firmada por dos frases
// sueltas ("Sin sedación. Sin dolor."). Por eso, con `sinHallazgos: true`, el
// patch solo siembra si trae al menos un hallazgo POSITIVO (sedación, DVA,
// soporte de O₂…): ahí el chequeo contradice la basal y el detalle manda,
// flip incluido. Si es todo normal, el sistema se salta SIN marcar sus claves:
// si la autora des-firma la basal después, la siembra cruza recién ahí.
//
// PROCEDENCIA del sembrado normal (regresión del orden REAL de trabajo, ola
// "El estándar Bupa", 2026-08-16): en el flujo verdadero la visita (F2) va
// ANTES que la Mesa (F4), así que cuando la enfermera llega a la Mesa el
// sistema YA trae los valores normales del chequeo ("Sin sedación", "Sin
// dolor") y la basal rica quedaba inalcanzable: el chip desaparecía y el
// documento imprimía dos frases sueltas en vez de la basal. Para que la Mesa
// pueda distinguir "esto lo sembró el puente y dice normal" de "esto lo
// respondió la enfermera", cada clave sembrada con valor normal queda anotada
// en `evolucion._sembradoNormal` (mismo patrón de claves `sistema:campo` que
// `_soportesEnrutados`). Clave OPCIONAL y aditiva: un estado viejo sin ella se
// comporta exactamente igual que antes (fijado por test).
// Devuelve { evolucion, sembrados }.
export function mergeSoportesEnEvolucion(ex, evolucion) {
  const base = evolucion && typeof evolucion === 'object' ? evolucion : { sistemas: {} };
  const porSistema = soportesParaEvolucion(ex);
  const entradas = Object.entries(porSistema);
  if (!entradas.length) return { evolucion: base, sembrados: 0 };

  const yaSembrados = new Set(base._soportesEnrutados || []);
  const vacio = (v) => v === undefined || v === null
    || (typeof v === 'string' && !v.trim()) || (Array.isArray(v) && !v.length);
  const sistemas = { ...(base.sistemas || {}) };
  const claves = [...(base._soportesEnrutados || [])];
  // EL LIBRO DE LA RECEPCIÓN (2026-08-23, encargo del fundador: "que la visita
  // de enfermería sea el punto de referencia de la evolución por sistema").
  // Este puente es el único que baja LA FOTO DEL RECIBIR: lee del examen de
  // INGRESO a propósito (ver correrPuentes), así que todo lo que escribe es,
  // por definición, lo que se constató al recibir el turno. Guardar el VALOR
  // exacto (y no solo la clave, como hacía `_soportesEnrutados`) es lo que
  // permite después preguntar "¿este campo sigue siendo la foto, o la autora
  // ya lo actualizó?" — sin esa respuesta no se puede anclar nada al pasado
  // sin arriesgarse a mandar al pasado algo que la autora acaba de escribir.
  // Libro PROPIO y no el `_puenteCampos` de turnoEvolucionBridge: ese lo
  // consulta `sembrarCampos` para decidir RE-SIEMBRA, y meter estas claves ahí
  // le daría permiso para pisar un soporte de la visita en la segunda pasada
  // (colisión real: el chequeo y los dispositivos escriben ambos
  // `ventilacion:viaArtificial`). Dos libros con dueños distintos, cero
  // ambigüedad de procedencia.
  const libro = { ...(base._puenteSoportes || {}) };
  // Solo lo que ESTE puente escribió de verdad y además dice normal. Si la
  // autora ya había respondido el campo, el valor es suyo y no entra: la
  // procedencia es el punto de la lista.
  const normales = [...(base._sembradoNormal || [])];
  let sembrados = 0;

  for (const [sisId, patch] of entradas) {
    const prev = sistemas[sisId] || {};
    // Basal firmada + chequeo todo-normal ⇒ no sembrar ni marcar (ver arriba).
    if (prev.sinHallazgos === true
      && Object.entries(patch).every(([k, v]) => soporteEsNormal(sisId, k, v))) {
      continue;
    }
    const campos = { ...(prev.campos || {}) };
    let cambio = false;
    for (const [k, v] of Object.entries(patch)) {
      const clave = `${sisId}:${k}`;
      if (yaSembrados.has(clave)) {
        // CORREGIR LA RECEPCIÓN NO ES REGISTRAR UN CAMBIO POSTERIOR (U4).
        // Marcar la clave y no volver a mirarla congelaba la foto: si la autora
        // se equivoca en el chequeo y lo corrige (llenó mirando la cama de al
        // lado, caso real), la evolución seguía diciendo cómo llegó un paciente
        // que no es este, y el documento abría con "Se recibió …" falso.
        // Se actualiza SOLO si lo que hay hoy en el campo es IDÉNTICO a lo
        // último que este puente escribió, o sea nadie lo tocó. Si la autora lo
        // cambió, su edición manda y el desacuerdo se DICE
        // (`conflictosRecepcion`), no se resuelve solo: misma regla que
        // `sembrarCampos`, y la doctrina de la casa es avisar, jamás retractar.
        if (!(clave in libro)) continue;
        if (JSON.stringify(libro[clave]) === JSON.stringify(v)) continue; // la fuente no cambió
        if (JSON.stringify(campos[k]) !== JSON.stringify(libro[clave])) continue; // lo tocó ella
        campos[k] = v;
        libro[clave] = v;
        cambio = true;
        sembrados += 1;
        continue;
      }
      claves.push(clave);
      if (!vacio(campos[k])) continue; // la autora ya respondió: no pisar
      campos[k] = v;
      libro[clave] = v;
      if (soporteEsNormal(sisId, k, v)) normales.push(clave);
      cambio = true;
      sembrados += 1;
    }
    if (cambio) {
      // Campos reales anulan el "sin hallazgos" del sistema (misma regla que
      // los otros puentes).
      sistemas[sisId] = { ...prev, campos, sinHallazgos: false };
    }
  }

  // Antes bastaba comparar el largo de las claves, porque este puente solo
  // agregaba. Con la re-siembra puede cambiar un valor sin agregar ninguna
  // clave, y devolver `base` ahí perdía la corrección en silencio.
  if (claves.length === (base._soportesEnrutados || []).length && !sembrados) {
    return { evolucion: base, sembrados: 0 };
  }
  const salida = { ...base, sistemas, _soportesEnrutados: claves };
  // La clave solo aparece cuando hay algo que anotar: sin sembrado normal, el
  // estado sale con la misma forma de siempre.
  if (normales.length) salida._sembradoNormal = normales;
  if (Object.keys(libro).length) salida._puenteSoportes = libro;
  return { evolucion: salida, sembrados };
}

// La recepción se corrigió y la autora ya había tocado ese campo (U4): las dos
// versiones son legítimas y ninguna se pisa, así que el desacuerdo se DICE.
// Derivado al leer, catálogo cerrado, sin texto libre: la misma forma que
// `conflictosTrayectoria` y `discrepanciasEstado`.
export function conflictosRecepcion(ex, evolucion) {
  const libro = evolucion?._puenteSoportes;
  if (!libro || typeof libro !== 'object') return [];
  const out = [];
  for (const [sisId, patch] of Object.entries(soportesParaEvolucion(ex))) {
    for (const [k, v] of Object.entries(patch)) {
      const clave = `${sisId}:${k}`;
      if (!(clave in libro)) continue;
      if (JSON.stringify(libro[clave]) === JSON.stringify(v)) continue; // la fuente no cambió
      const actual = evolucion?.sistemas?.[sisId]?.campos?.[k];
      if (JSON.stringify(actual) === JSON.stringify(libro[clave])) continue; // nadie lo tocó: se re-siembra sola
      out.push({ sistema: sisId, campo: k, tipo: 'recepcion_editada' });
    }
  }
  return out;
}

// ¿Este campo SIGUE SIENDO la foto del recibir? Devuelve el valor que la visita
// constató si nadie lo tocó desde entonces, o null si la autora ya lo actualizó
// (o si nunca lo sembró este puente). Es la primitiva de procedencia sobre la
// que se apoya el anclaje temporal de la prosa ("Se recibió con vía aérea
// nativa…" en vez de afirmarla como presente).
//
// FAIL-SAFE hacia el PRESENTE, igual que toda la familia de omisión con
// portador vivo: ante cualquier duda devuelve null y el campo se sigue narrando
// como hoy. Los borradores anteriores a este libro no traen `_puenteSoportes`,
// así que devuelven null para todo y su documento sale byte a byte idéntico
// (regla del repo: clave nueva ausente ⇒ output idéntico, fijado por test).
export function valorRecibido(evolucion, sisId, campo) {
  const libro = evolucion?._puenteSoportes;
  if (!libro || typeof libro !== 'object') return null;
  const clave = `${sisId}:${campo}`;
  if (!(clave in libro)) return null;
  const actual = evolucion?.sistemas?.[sisId]?.campos?.[campo];
  return JSON.stringify(actual) === JSON.stringify(libro[clave]) ? libro[clave] : null;
}

// Todas las claves de un sistema que siguen siendo la foto del recibir.
// → ['sedacion', 'sedoanalgesia', …]
export function camposRecibidos(evolucion, sisId) {
  const libro = evolucion?._puenteSoportes;
  if (!libro || typeof libro !== 'object') return [];
  const pref = `${sisId}:`;
  return Object.keys(libro)
    .filter((k) => k.startsWith(pref))
    .map((k) => k.slice(pref.length))
    .filter((campo) => valorRecibido(evolucion, sisId, campo) !== null);
}

// ── Cambios entre el examen de ingreso y el de control (dictado 2026-07-27) ──
// Dos fotos → la película: la evolución del final se genera a partir de los
// CAMBIOS entre ambas. Todo determinista y catálogo cerrado + números: cero
// IA para detectar un cambio. Invariante intocada: el silencio del control
// sigue diciendo "no evaluado" — un hallazgo solo se declara remitido si el
// segmento del control SE EXAMINÓ (tiene contenido); jamás se afirma
// normalidad de lo no constatado.
const SISTEMA_ESCALA = {
  glasgow: 'neuro', rass: 'neuro', four: 'neuro', morse: 'neuro', downton: 'neuro',
  braden: 'tegumentario',
};

export function cambiosEntreExamenes(ingreso, control) {
  const cambios = []; // { sistema, clave (idempotencia), texto }
  if (!ingreso || !control || !examenTieneContenido(control)) return cambios;

  // Conciencia: el cambio de estado va a neuro con su "desde dónde".
  if (ingreso.conciencia && control.conciencia && control.conciencia !== ingreso.conciencia) {
    cambios.push({
      sistema: 'neuro',
      clave: 'conciencia',
      texto: `conciencia ${lowerFirst(control.conciencia)} (al recibir ${lowerFirst(ingreso.conciencia)})`,
    });
  }

  // Hallazgos que REMITEN: estaban al ingreso y el control re-examinó el
  // segmento sin ellos. (Los que APARECEN ya los trae mergeExamenEnEvolucion
  // con la hora del control — no se duplican aquí.)
  for (const seg of SEGMENTOS_EXAMEN) {
    const antes = segmentoExamenState(ingreso, seg.id).hallazgos;
    if (!antes.length || !segmentoConContenido(control, seg)) continue;
    const ahora = segmentoExamenState(control, seg.id).hallazgos;
    for (const h of seg.hallazgos) {
      if (!h.evolucion || !antes.includes(h.v) || ahora.includes(h.v)) continue;
      cambios.push({
        sistema: h.evolucion,
        clave: `remite:${claveHallazgo(seg.id, h.v)}`,
        texto: `remite ${lowerFirst(h.v)} (${lowerFirst(seg.label)})`,
      });
    }
  }

  // Escalas re-medidas en el control: el delta usa el MISMO motor (scoreEscala)
  // y su mismo texto — cero re-derivación de cortes.
  for (const [id, valsCtrl] of Object.entries(escalasExamenState(control))) {
    const sis = SISTEMA_ESCALA[id];
    if (!sis || !escalaConValores(id, valsCtrl)) continue;
    const resCtrl = scoreEscala(id, valsCtrl);
    if (!resCtrl?.texto) continue;
    const valsIng = escalasExamenState(ingreso)[id];
    const resIng = valsIng && escalaConValores(id, valsIng) ? scoreEscala(id, valsIng) : null;
    if (!resIng?.texto || resIng.texto === resCtrl.texto) continue;
    cambios.push({
      sistema: sis,
      clave: `escala:${id}`,
      texto: `${resCtrl.texto} (al recibir ${resIng.texto})`,
    });
  }

  return cambios;
}

// Vuelca los cambios como línea "Al examen de control: …" en su
// sistema. Idempotente vía `evolucion._controlEnrutado` (mismo patrón que
// `_examenEnrutado`); agrega, nunca pisa. Pura.
export function mergeCambiosControlEnEvolucion(ingreso, control, evolucion) {
  const base = evolucion && typeof evolucion === 'object' ? evolucion : { sistemas: {} };
  const ya = new Set(base._controlEnrutado || []);
  const pendientes = cambiosEntreExamenes(ingreso, control)
    .filter((c) => !ya.has(c.clave) && SISTEMAS_EVOLUCION.some((s) => s.id === c.sistema));
  if (!pendientes.length) return { evolucion: base, agregadas: 0 };

  // Misma norma que el puente del ingreso: el contexto ancla, la hora no
  // se imprime (queda sellada en control.hora / control.ts).
  const sistemas = { ...(base.sistemas || {}) };
  const claves = [...(base._controlEnrutado || [])];
  const porSistema = {};
  for (const c of pendientes) (porSistema[c.sistema] = porSistema[c.sistema] || []).push(c);
  let agregadas = 0;

  for (const [sisId, lista] of Object.entries(porSistema)) {
    const prev = sistemas[sisId] || { hallazgos: [], nota: '' };
    // Misma concordancia de género que el puente del examen de ingreso: la
    // frase la escribe MiPAE, no la autora. Ver la nota de mergeExamenEnEvolucion.
    const frase = (base?.sexo === 'F' ? generizarFemenino : (t) => t)(
      `Al examen de control: ${lista.map((c) => c.texto).join('; ')}.`,
    );
    const notaPrev = typeof prev.nota === 'string' ? prev.nota.trim() : '';
    sistemas[sisId] = { ...prev, nota: notaPrev ? `${notaPrev}\n${frase}` : frase, sinHallazgos: false };
    for (const c of lista) { claves.push(c.clave); agregadas += 1; }
  }

  return { evolucion: { ...base, sistemas, _controlEnrutado: claves }, agregadas };
}

// ── Voz primero: "Habla tu examen" ────────────────────────────────────────────
// Parser DETERMINISTA (cero IA, puro, testeable) del recorrido dictado — fiel al
// ADN del módulo: solo el catálogo cerrado aterriza. Reconoce la apertura
// (impresión / conciencia / posición) y los hallazgos POR SEGMENTO, y jamás
// vuelca el transcrito crudo a una nota (misma garantía dura del puente: texto
// libre del paciente no entra a un campo estructurado — evita que un nombre o
// RUT dictado quede guardado). Lo que no reconoce se queda en el transcrito
// visible para que la enfermera lo agregue a mano. Sobre la regla "el silencio
// dice no evaluado": un "resto normal" DICHO no es silencio sino una
// declaración explícita, así que sí honra un normal global explícito — pero
// nunca inventa normalidad sobre lo que no se nombró.

const _norm = (t) => String(t || '')
  .toLowerCase()
  .normalize('NFD').replace(/[̀-ͯ]/g, '') // saca los diacríticos
  .replace(/\s+/g, ' ')
  .trim();

// Apertura: el primer match gana; el orden va de lo más grave/específico a lo
// más genérico (sopor antes que vigil, semisentado antes que sentado).
const _IMPRESION_CUES = [
  // "Mal estado" a secas también describe otras cosas ("piezas dentarias en mal
  // estado"): se exige "general" o que se diga del paciente.
  ['Mal estado general', ['mal estado general', 'muy comprometid', 'paciente en mal estado']],
  ['Regular estado general', ['regular estado general', 'regulares condiciones', 'paciente en regular estado']],
  ['Buen estado general', ['buen estado general', 'buenas condiciones', 'paciente en buen estado']],
];
const _CONCIENCIA_CUES = [
  ['No responde', ['no responde', 'inconsciente', 'no reactivo']],
  ['Sopor', ['soporoso', 'sopor']],
  ['Agitación psicomotora', ['agitad', 'agitacion psicomotora']],
  // Sin 'adormecid': "mano adormecida" no es somnolencia.
  ['Somnoliento', ['somnolient']],
  // OJO: nada de 'orientad' aquí — 'desorientado' lo contiene y daría un vigil
  // falso; la desorientación tampoco es hallazgo de cabeza (se retiró del
  // catálogo): si se dicta, simplemente no aterriza en ningún campo.
  ['Vigil y conectado', ['vigil', 'consciente', 'lucid', 'despiert', 'alerta']],
];
const _POSICION_CUES = [
  ['Semisentado', ['semisentad', 'semi sentad', 'fowler']],
  ['Decúbito lateral', ['decubito lateral', 'de lado', 'lateralizad']],
  ['Prono', ['decubito prono', 'boca abajo', 'prono']],
  ['Deambulando', ['deambul', 'caminando']],
  ['Sentado', ['sedente', 'sentad']],
  ['Decúbito supino', ['decubito supino', 'decubito dorsal', 'boca arriba', 'acostad', 'supino']],
];

// Misma lectura que los hallazgos (`_presente`, más abajo): la pista empieza
// palabra y nada la niega. Con `includes` suelto, "vigilar" daba un vigil y
// "no agitado", agitación psicomotora.
function _matchPrimero(norm, cues) {
  for (const [valor, tokens] of cues) {
    if (tokens.some((tk) => _presente(norm, tk))) return valor;
  }
  return null;
}

// Arma las formas estructura × calificativo para no escribirlas a mano.
const _combinar = (bases, calificativos) => bases.flatMap((b) => calificativos.map((c) => `${b} ${c}`));

// La lesión del talón se dice casi siempre ANTES de nombrar el talón ("eritema
// en el talón derecho"), y el tramo de sacra abría en la palabra "talón": lo
// dicho antes caía fuera. Por eso estas frases abren el tramo de sacra Y son la
// pista del hallazgo. "Talones" a secas es la zona, no la lesión ("talones
// indemnes"), y no anota nada.
const _LESION_TALON = [
  ..._combinar(
    [
      'eritema', 'lpp', 'upp', 'lesion', 'lesiones', 'ulcera', 'ulceras', 'escara', 'escaras', 'flictena', 'flictenas',
      'lesion por presion', 'ulcera por presion', 'enrojecimiento',
      ..._combinar(['lpp categoria', 'upp categoria', 'lpp grado', 'upp grado'], ['1', '2', '3', '4']),
    ],
    ['en talon', 'en el talon', 'en los talones', 'en ambos talones', 'de talon', 'del talon', 'de los talones'],
  ),
  ..._combinar(
    ['talon con', 'talones con', 'talon derecho con', 'talon izquierdo con'],
    ['eritema', 'lpp', 'upp', 'lesion', 'ulcera', 'escara', 'flictena'],
  ),
  ..._combinar(['talon', 'talones', 'talon derecho', 'talon izquierdo'], ['enrojecid', 'eritematos']),
];
const _ABRE_TALON = new Set(_LESION_TALON);

// Pistas de segmento: la palabra con la que la enfermera nombra la zona. Se
// evita 'cara' suelta (aparece dentro de otras palabras) y 'extremidades' a
// secas (ambigua superior/inferior): se exige el calificador.
const _SEGMENTO_CUES = [
  ['sacra', ['zona sacra', 'region sacra', 'sacra', 'sacro', 'coxis', 'coccix', 'puntos de apoyo', 'zonas de apoyo', 'zona de apoyo', 'talon', 'talones', ..._LESION_TALON]],
  ['espalda', ['espalda', 'region dorsal', 'dorso', 'columna']],
  ['genitales', ['genital', 'perineal', 'perineo', 'perine']],
  ['eeii', ['extremidades inferiores', 'extremidad inferior', 'miembros inferiores', 'piernas', 'pierna', 'eeii']],
  ['eess', ['extremidades superiores', 'extremidad superior', 'miembros superiores', 'brazos', 'brazo', 'eess']],
  ['abdomen', ['abdomen', 'abdominal', 'vientre', 'panza']],
  ['torax', ['torax', 'toracic', 'pecho', 'pulmonar', 'pulmon', 'respiratori', 'campos pulmonares']],
  ['cuello', ['cuello', 'cervical']],
  ['cabeza', ['cabeza', 'craneo', 'facial', 'cefalic', 'cuero cabelludo']],
];

// El contexto de una lesión de talón es SOLO lo que la niega pegado ("sin",
// "ni", "no presenta") o la vuelve riesgo ("riesgo de", "prevención de"), en
// las palabras justo antes. Toda la oración traía el "sin edema" de las
// extremidades y callaba "sin edema lpp categoría 2 en talón izquierdo".
function _contextoTalon(norm, idx) {
  const antes = norm.slice(_inicioTramo(norm, idx, _FIN_ORACION), idx);
  const m = antes.match(_NEGADOR_PEGADO) || antes.match(_RIESGO_PEGADO);
  return m ? idx - m[0].length : idx;
}

// Reparte el transcrito en tramos por segmento: cada pista abre un tramo que
// corre hasta la siguiente. Solo la PRIMERA aparición de cada segmento cuenta.
//
// Un tramo abierto por una lesión de talón ("sin lesiones en talones") trae
// además lo dicho antes en su oración, como CONTEXTO: sin él, el "sin" quedaba
// en el segmento anterior y la lesión negada se anotaba (revisión adversarial,
// 2026-10-27). `texto` empieza en el contexto y `desde` marca dónde empieza el
// tramo de verdad: las pistas se buscan solo desde ahí.
function _segmentSpans(norm) {
  const marcas = [];
  for (const [id, tokens] of _SEGMENTO_CUES) {
    let idx = -1;
    let abre = null;
    for (const tk of tokens) {
      const i = norm.indexOf(tk);
      if (i >= 0 && (idx < 0 || i < idx)) { idx = i; abre = tk; }
    }
    if (idx >= 0) marcas.push({ id, idx, abre });
  }
  marcas.sort((a, b) => a.idx - b.idx);
  return marcas.map((m, i) => {
    const contexto = _ABRE_TALON.has(m.abre) ? _contextoTalon(norm, m.idx) : m.idx;
    return {
      id: m.id,
      texto: norm.slice(contexto, i + 1 < marcas.length ? marcas[i + 1].idx : undefined),
      desde: m.idx - contexto,
    };
  });
}

// Sinónimos por hallazgo (normalizados). Las claves compartidas entre segmentos
// (Edema, Frialdad distal, Lesiones de piel…) funcionan porque el match es
// POR TRAMO: 'edema' solo se busca dentro del tramo de la extremidad.
//
// Una pista nombra el HALLAZGO, no la estructura (2026-10-27): "llene
// capilar", "pulsos distales", "ruidos hidroaéreos", "mucosa oral", "cuero
// cabelludo", "yugular" o "talón" a secas se dicen igual cuando están bien, y
// anotaban el hallazgo anormal. Ahora exigen el calificativo; `_combinar`
// (arriba) arma las formas (estructura × calificativo).
const _SINONIMOS_HALLAZGO = {
  'Mucosas resecas': ['mucosas secas', 'mucosas resecas', 'mucosa seca', 'boca seca', 'deshidratad'],
  'Palidez de piel': ['palidez', 'palid'],
  // Sin 'amarillent' suelto: "secreciones amarillentas" no es ictericia.
  'Ictericia en escleras': ['icteric', 'escleras amarill', 'piel amarillenta', 'tinte amarillento'],
  'Cianosis peribucal': ['cianosis', 'cianotic', 'labios morados'],
  'Edema facial': ['edema facial', 'cara hinchada', 'hinchazon facial'],
  // 'Desorientación' salió del catálogo (es estado de conciencia, no hallazgo
  // de cabeza): 'desorientad'/'confus' ya no aterrizan en ningún campo — y
  // siguen sin dar un 'Vigil' falso (ver _CONCIENCIA_CUES).
  'Herida o hematoma facial': ['hematoma', 'herida facial', 'herida en la cara', 'contusion facial'],
  'Drenaje presente': ['drenaje'],
  'Lesiones en cuero cabelludo o cara': [
    ..._combinar(['lesion', 'lesiones', 'herida', 'heridas'], ['en cuero cabelludo', 'en el cuero cabelludo', 'en la cabeza', 'en la cara']),
    ..._combinar(['cuero cabelludo con'], ['lesion', 'herida']),
  ],
  'Ingurgitación yugular': ['ingurgitacion', 'yugular ingurgitada', 'yugulares ingurgitadas'],
  'Adenopatías palpables': ['adenopat', ..._combinar(['ganglio', 'ganglios'], ['palpable', 'aumentado', 'inflamado'])],
  'Rigidez o dolor cervical': ['rigidez de nuca', 'dolor cervical', 'rigidez cervical', 'rigidez'],
  'Desviación traqueal': ['desviacion traqueal', 'traquea desviada'],
  'Aumento de volumen cervical': ['aumento de volumen', 'masa cervical', 'bocio'],
  'Herida o apósito cervical': ['aposito cervical', 'herida cervical', 'apacito cervical'],
  'Asimetría en la expansión': ['asimetr', 'expansion asimetr', 'no expande'],
  'Uso de musculatura accesoria': ['musculatura accesoria', 'musculos accesorios', 'uso de accesoria'],
  'Uso de musculatura abdominal': ['musculatura abdominal'],
  'Tiraje subcostal': ['tiraje subcostal'],
  'Tiraje supraclavicular': ['tiraje supraclavicular', 'tiraje clavicular'],
  'Respiración rápida y superficial': ['taquipne', 'polipne', 'respiracion rapida', 'respiracion superficial'],
  'Tiraje intercostal': ['tiraje intercostal', 'retraccion', 'retracciones', 'tiraje'],
  'Drenaje pleural aspirativo': ['drenaje pleural aspirativ', 'pleurostomia aspirativ'],
  'Drenaje pleural a caída libre': ['caida libre'],
  'Lesiones en mucosa oral u orofaríngea': [
    'mucositis', 'afta', 'ulcera oral', 'ulceras orales', 'lesion oral', 'lesiones orales',
    ..._combinar(['lesion', 'lesiones'], ['en mucosa', 'en la mucosa', 'en la boca', 'en orofaring', 'en la orofaring']),
    ..._combinar(['mucosa oral con', 'orofaringe con'], ['lesion', 'ulcera']),
  ],
  'Usa placa dental': ['placa dental', 'protesis dental'],
  'Tubo orotraqueal in situ': ['tubo orotraqueal', 'tot in situ', 'intubado', 'tubo endotraqueal'],
  // "Fijación adecuada" a secas puede ser de la SNG o de la mascarilla.
  'Fijación del tubo adecuada': ['fijacion del tubo adecuada', 'fijacion adecuada del tubo', 'tubo con fijacion adecuada', 'tot con fijacion adecuada', 'tubo bien fijado', 'tubo bien fijo', 'tubo fijo'],
  // "Tos" a secas no dice secreciones ("tos seca" las niega).
  'Tos con secreciones': ['tos con secrecion', 'tos productiva', 'tos humeda', 'expectora', 'secrecion bronquial', 'secreciones bronquiales', 'secreciones respiratorias'],
  'Herida operatoria con apósitos': ['herida operatoria', 'herida quirurgica', 'aposito operatorio', 'herida qx'],
  'Apósitos manchados': ['aposito manchado', 'apositos manchados', 'manchad', 'aposito con exudado'],
  'Lesiones de piel en tórax': ['lesion de piel', 'lesiones de piel', 'lesion cutanea'],
  'Distensión abdominal': ['distension', 'distendid', 'abdomen tenso'],
  'Drenaje abdominal presente': ['drenaje abdominal'],
  'Sensibilidad alterada': ['sensibilidad alterada', 'parestesia', 'hormigueo'],
  'Equimosis': ['equimosis', 'moreton'],
  'Eritema': ['eritema en'],
  'Flictenas': ['flictena'],
  'Piel reseca': ['piel seca', 'piel reseca'],
  'Edema localizado': ['edema localizado'],
  'Edema bilateral': ['edema bilateral', 'edema en ambas'],
  'Godet (+)': ['godet leve'],
  'Godet (++)': ['godet moderado'],
  'Godet (+++)': ['godet severo'],
  'Dolor a la palpación': ['dolor a la palpacion', 'doloroso a la palpacion', 'sensible a la palpacion', 'abdomen doloroso', 'palpacion dolorosa'],
  'Ruidos hidroaéreos disminuidos': [
    ..._combinar(['ruidos hidroaereos', 'ruidos intestinales', 'rha', 'peristalsis'], ['disminuid', 'ausente', 'abolid', 'escas']),
    'sin rha', 'sin ruidos hidroaereos', 'sin ruidos intestinales', 'sin peristalsis',
  ],
  'Ostomía presente': ['ostom', 'colostom', 'ileostom'],
  'Globo vesical': ['globo vesical', 'vejiga distendida'],
  'Edema': ['edema', 'hinchad'],
  'Frialdad distal': ['frialdad', 'frio distal', 'extremidades frias', 'fria al tacto'],
  'Llene capilar enlentecido': _combinar(
    ['llene capilar', 'llenado capilar', 'relleno capilar', 'llene'],
    ['enlentecid', 'lento', 'prolongad', 'retardad', 'aumentad', 'mayor'],
  ),
  'Debilidad o paresia': ['debilidad', 'paresia', 'paretic', 'plejia', 'no mueve'],
  'Movilidad disminuida': ['movilidad disminuida', 'no moviliza', 'limitacion de movilidad', 'poca movilidad', 'rigidez articular'],
  'Lesiones de piel': ['lesion de piel', 'lesiones de piel', 'lesion cutanea', 'dermatitis'],
  'Sitio de punción con signos inflamatorios': [
    'flebitis', 'via inflamada', 'via venosa inflamada',
    ..._combinar(['sitio de puncion', 'punto de puncion', 'sitio de insercion'], ['con signos', 'inflamad', 'enrojecid', 'eritematos', 'con eritema', 'con flebitis', 'doloroso', 'con dolor']),
  ],
  'Pulsos distales disminuidos': [
    ..._combinar(['pulsos distales', 'pulso distal', 'pulsos pedios', 'pulso pedio', 'pulsos', 'pulso'], ['disminuid', 'debil', 'ausente', 'filiforme', 'apenas palpable', 'no palpable']),
    'sin pulso', 'ausencia de pulso',
  ],
  'Signos de TVP (dolor o empastamiento)': ['tvp', 'empastamiento', 'trombosis', 'pantorrilla dolorosa'],
  'Úlcera o herida en pierna o pie': ['ulcera venosa', 'ulcera arterial', 'herida en la pierna', 'herida en el pie', 'ulcera en la pierna', 'pie diabetico'],
  'Eritema perineal (DAI)': ['dai', 'eritema perineal', 'dermatitis del panal', 'dermatitis asociada'],
  'Edema genital': ['edema genital', 'genitales edematosos', 'escroto edematoso'],
  'Secreción o sangrado': ['secrecion', 'sangrado', 'sangramiento', 'flujo genital'],
  'Signos de infección perineal': ['infeccion perineal', 'absceso perineal'],
  'Piel macerada por humedad': ['macerad', 'piel humeda', 'exceso de humedad'],
  'Eritema en zonas de apoyo': ['eritema', 'enrojecimiento'],
  'Deformidad o desviación de columna': ['deformidad', 'desviacion de columna', 'escoliosis', 'cifosis'],
  // BUG FIX 2026-07-18: el token pelado 'eritema'/'enrojecimiento' se retiró.
  // Un "eritema" genérico dictado (que BLANQUEA, reactivo, benigno) aterrizaba
  // como "Eritema que no blanquea" — la bandera roja de LPP Categoría I en la
  // zona médico-legal. Ahora solo el eritema CALIFICADO como no-blanqueable
  // dispara el hallazgo; el genérico no registra nada (más seguro sub-matchear
  // que falsear una LPP).
  'Eritema que no blanquea': ['no blanquea', 'eritema que no blanquea', 'categoria 1', 'lpp 1'],
  'LPP presente al recibir': ['lpp', 'upp', 'ulcera por presion', 'escara', 'lesion por presion', 'ulcera de decubito'],
  'Lesión por presión en desarrollo (DTPI)': ['dtpi', 'tejido profundo'],
  'Apósito de protección instalado': ['aposito de proteccion', 'apacito de proteccion', 'proteccion instalada', 'aposito preventivo'],
  'LPP o eritema en talones': _LESION_TALON, // ver arriba, junto a las pistas de segmento
};

// ── Cómo se lee una pista (fundador, 2026-10-27) ─────────────────────────────
// El lector anotaba hallazgos que nadie dijo, y casi siempre el contrario del
// dicho. Una sonda con frases de examen normales lo mostró en tres formas:
//  · buscaba la pista DENTRO de otras palabras: "crépitos", "estos" y
//    "puntos" traían "tos"; "anictérico", "ictérico"; "acianótico", "cianótico".
//  · el "sin" negaba solo la palabra pegada: "sin ingurgitación yugular"
//    anotaba la ingurgitación (por "yugular") y "sin palidez ni cianosis", la
//    cianosis. El "no" pegado ("no distendido") y la negación dicha después
//    ("adenopatías no palpables") no se miraban.
//  · varias pistas nombraban la ESTRUCTURA y no el hallazgo: "llene capilar
//    menor a 2 segundos" anotaba el llene enlentecido, "pulsos distales
//    presentes" los pulsos disminuidos, "mucosa oral hidratada" lesiones
//    (esas se arreglan en la tabla de sinónimos, exigiendo el calificativo).
// La doctrina es la misma del eritema de más arriba: más seguro quedarse
// corto que falsear. Lo que el lector calla queda escrito a la vista en la
// revisión de "Habla tu examen", para marcarlo a mano.

const _LETRA = /[a-z0-9]/; // el texto ya viene sin tildes ni mayúsculas (_norm)

// Pistas que SÍ pueden ir dentro de una palabra: el prefijo dice dónde, no
// cambia el hallazgo (hemi-paresia, para-plejia, gastr-ostomía).
const _PISTAS_SUFIJO = new Set(['paresia', 'paretic', 'plejia', 'ostom']);
// Pistas que tienen que ser la palabra ENTERA: "vigil" vive en "vigilar",
// "sopor" en "soporte" (y con "soporte ventilatorio" anotaba sopor) y "prono"
// en "pronóstico".
const _PISTAS_PALABRA = new Set(['vigil', 'sopor', 'prono']);

// Negadores: los de evolucionDictado.js (NEGACION) y las formas del examen
// ("no se pesquisa", "no impresiona", "se descarta"), con la diferencia que
// allá ya quedó escrita: aquí el "no" suelto NO niega la cláusula, porque en el
// examen "no blanquea" ES el hallazgo. Solo niega pegado a la pista.
const _NEGADOR_SRC = 'sin|ni|niega|nunca|tampoco|jamas|ningun\\w*|nada de|ausencia de|se descartan?|descarta'
  + '|no (?:hay|presentan?|tienen?|refiere|impresiona|se (?:observan?|aprecian?|palpan?|evidencian?|pesquisan?|constatan?|objetivan?))';
const _NEGADOR = new RegExp(`\\b(?:${_NEGADOR_SRC})\\b`);
// Negador PEGADO a la pista ("sin edema", "ni cianosis", "no distendido", "sin
// signos de flebitis"): la negación es clara.
const _NEGADOR_PEGADO = new RegExp(`\\b(?:${_NEGADOR_SRC}|no)(?:\\s+(?:signos|evidencia|presencia|datos|uso))?(?:\\s+(?:de|del))?\\s+$`);
// Cierran la negación: lo que viene después se afirma ("sin dolor, pero con
// ostomía"; "sin edema con frialdad distal").
const _CIERRA_NEGACION = /\b(pero|con|aunque|salvo|excepto)\b/g;
// La negación dicha después del hallazgo, pegada a él o tras un calificativo
// de lugar ("adenopatías cervicales no palpables", "edema maleolar ausente").
// Solo de lugar a propósito: "frío distal pulsos ausentes" sin coma no puede
// negar la frialdad.
const _NEGADO_DESPUES = /^\s*(?:(?:cervical(?:es)?|peribucal|bilateral(?:es)?|distal(?:es)?|pedios?|inguinal(?:es)?|axilar(?:es)?|facial(?:es)?|maleolar(?:es)?|pretibial(?:es)?|palpebral(?:es)?|periorbitari[oa]s?|generalizad[oa]s?)\s+)?(?:ausentes?|negativ[oa]s?|no palpables?|no se palpan?|no se observan?|no visibles?|descartad[oa]s?|(?:grado |escala )?(?:0|cero))\b/;
// Riesgo, prevención y sospecha no son el hallazgo: "riesgo alto de
// desarrollar LPP", "profilaxis de TVP", "eco doppler para descartar TVP", "no
// se descarta TVP" (que NO es una negación: es lo contrario).
const _NO_ES_HALLAZGO_ANTES = /\b(riesgo|prevencion|prevenir|evitar|profilaxis|descartar|sospecha|probable|posible|no se descartan?)\b(?:\s+\S+){0,4}\s*$/;
const _SOLO_RIESGO_ANTES = /\b(riesgo|prevencion|prevenir|evitar|profilaxis)\b(?:\s+\S+){0,4}\s*$/;
// "Riesgo de" o "prevención de" pegado a la pista (contexto del talón).
const _RIESGO_PEGADO = /\b(riesgo|prevencion)(?:\s+\S+)?\s+de\s+$/;
// La DTPI se nombra justamente como sospecha ("sospecha de DTPI"): ahí la
// sospecha ES el hallazgo, y solo el riesgo o la prevención la callan.
const _SOSPECHA_ES_EL_HALLAZGO = new Set(['dtpi', 'tejido profundo']);

// La coma corta la negación ("Cuello sin adenopatías, yugulares ingurgitadas"
// afirma la ingurgitación), salvo en una LISTA de ausencias bajo un solo
// "sin" ("sin palidez, cianosis o ictericia"; "sin edema, frialdad ni
// cianosis"): ítems cortos y un "ni/o/y" que la cierra. El punto la corta
// siempre. `_norm` ya juntó los saltos de línea en espacios.
const _FIN_ORACION = /[;.:!?]/;
const _FIN_CLAUSULA = /[,;.:!?]/;
function _inicioTramo(texto, i, re) {
  let j = i - 1;
  while (j >= 0 && !re.test(texto[j])) j -= 1;
  return j + 1;
}
function _finTramo(texto, i, re) {
  let j = i;
  while (j < texto.length && !re.test(texto[j])) j += 1;
  return j;
}

// Lo que queda después del último "pero/con/…": lo de antes ya se cerró.
function _trasUltimoCierre(texto) {
  let ambito = texto;
  let m;
  const cierres = new RegExp(_CIERRA_NEGACION.source, 'g');
  while ((m = cierres.exec(texto)) !== null) ambito = texto.slice(m.index + m[0].length);
  return ambito.replace(/\bsin embargo\b/g, '');
}

// Lo dicho ANTES, en su cláusula: null (nada la niega), 'clara' (negador
// pegado) o 'duda' (un negador más atrás sin un "pero/con" que lo cierre). Una
// coordinación ("sin edema y frialdad") NO cierra: es la duda que se calla,
// misma regla que evolucionDictado.js.
function _negacionAntes(antes) {
  if (_NEGADOR_PEGADO.test(antes)) return 'clara';
  return _NEGADOR.test(_trasUltimoCierre(antes)) ? 'duda' : null;
}

// ¿La pista es un ítem de una lista de ausencias que cruza comas? `previas`
// son las cláusulas de la oración antes de la suya (de la más lejana a la más
// cercana), `propia` lo dicho antes en su cláusula y `resto` lo que sigue en
// la oración. Ítems de hasta tres palabras, un negador que abre la lista y un
// "ni/o/y" que la cierra.
// El primer ítem, el que abre el negador, puede ser más largo ("sin signos de
// flebitis en sitio de punción, edema o equimosis"): se cuenta sin "signos
// de", "uso de" y parecidos, hasta cinco palabras. Y un ítem con un número o
// un "+" describe lo que hay ("LPP categoría 2", "edema ++"): no es de lista.
const _palabras = (t) => t.trim().split(/\s+/).filter(Boolean).length;
const _CORTO = (t, max = 3) => _palabras(t) <= max;
const _RELLENO_LISTA = /\b(?:signos|uso|evidencia|presencia|datos) del?\b/g;
function _enListaNegada(previas, propia, resto) {
  if (!_CORTO(propia) || /\b(con|pero|aunque|salvo|excepto)\b/.test(propia)) return false;
  const hastaCierre = resto.split(/\b(con|pero)\b/)[0];
  const item = `${propia} ${hastaCierre.split(/,|\b(?:ni|o|y)\b/)[0]}`;
  if (/[0-9+]/.test(item)) return false;
  const cierra = /\b(ni|o|y)\b/.test(propia) || /^[^.;:!?]*?\b(ni|o|y)\b/.test(hastaCierre);
  if (!cierra) return false;
  for (let k = previas.length - 1; k >= 0; k -= 1) {
    const c = previas[k];
    if (/\b(con|pero|aunque|salvo|excepto)\b/.test(c)) return false;
    const neg = c.search(_NEGADOR);
    if (neg >= 0) return _CORTO(c.slice(neg).replace(_NEGADOR, '').replace(_RELLENO_LISTA, ''), 5);
    if (!_CORTO(c)) return false;
  }
  return false;
}

// "Frialdad ni cianosis": el "ni" reparte la negación hacia atrás, pero solo
// si es el PRIMER negador que sigue y está cerca. En "LPP categoría 2 sin
// lesiones satélite ni signos de infección" el "ni" es del "sin", no de la
// LPP (la primera versión de este arreglo la callaba y la zona quedaba "piel
// indemne": revisión adversarial, 2026-10-27). Las pistas que traen su propio
// "no" o "sin" siguen con el "ni": "no responde a estímulos verbales ni
// dolorosos" ES el hallazgo.
const _CORTA_NI = /^(sin|no|niega|nunca|tampoco|jamas|ningun\w*|pero|con|aunque|salvo|excepto|y|e|o)$/;
function _niDespues(despues, tk) {
  if (/^(no|sin)\s/.test(tk)) return false;
  for (const p of despues.trim().split(/\s+/).slice(0, 4)) {
    if (p === 'ni') return true;
    if (_CORTA_NI.test(p)) return false;
  }
  return false;
}

// Cómo quedó una pista en el tramo: 'si' (se dijo), 'no' (se negó claro:
// "sin edema", "adenopatías no palpables"), 'duda' (se calló por una negación
// que no es clara, o por riesgo o prevención) o null (no aparece). La duda
// importa aparte: un segmento con una duda no se puede declarar normal.
function _lectura(span, tk, desde = 0) {
  let resultado = null;
  let from = desde;
  for (;;) {
    const i = span.indexOf(tk, from);
    if (i < 0) return resultado;
    from = i + 1;
    if (!_PISTAS_SUFIJO.has(tk) && i > 0 && _LETRA.test(span[i - 1])) continue;
    let fin = i + tk.length;
    if (_PISTAS_PALABRA.has(tk) && fin < span.length && _LETRA.test(span[fin])) continue;
    while (fin < span.length && _LETRA.test(span[fin])) fin += 1; // completa la palabra
    const iniOracion = _inicioTramo(span, i, _FIN_ORACION);
    const iniClausula = _inicioTramo(span, i, _FIN_CLAUSULA);
    const antes = span.slice(iniClausula, i);
    const previas = iniClausula > iniOracion ? span.slice(iniOracion, iniClausula - 1).split(',') : [];
    const despues = span.slice(fin, _finTramo(span, fin, _FIN_CLAUSULA));
    const resto = span.slice(fin, _finTramo(span, fin, _FIN_ORACION));
    // Solo una pista que EMPIEZA con "sin" o "con" ("sin rha", "con fuga
    // aérea") abre su propia predicación: lo negado antes no la alcanza. Un
    // "con" a media pista no basta ("sin tos con secreciones" niega la tos).
    // "Talón derecho con eritema" también: la zona nombrada y su "con".
    const abrePropia = /^(sin|con)\s/.test(tk) || /^talon\w*(?: (?:derecho|izquierdo))? con\s/.test(tk);
    const antesNiega = abrePropia ? null : _negacionAntes(antes);
    const enLista = !abrePropia && !antesNiega && _enListaNegada(previas, antes, resto);
    const noEsHallazgo = (_SOSPECHA_ES_EL_HALLAZGO.has(tk) ? _SOLO_RIESGO_ANTES : _NO_ES_HALLAZGO_ANTES).test(_trasUltimoCierre(antes));
    if (antesNiega === 'duda' || enLista || _niDespues(despues, tk) || noEsHallazgo) {
      resultado = 'duda';
      continue;
    }
    if (antesNiega === 'clara' || _NEGADO_DESPUES.test(despues)) {
      if (!resultado) resultado = 'no';
      continue;
    }
    return 'si';
  }
}

const _presente = (span, tk) => _lectura(span, tk) === 'si';

// Los hallazgos del tramo, y si quedó alguna duda (ver `_lectura`).
function _matchHallazgos(segId, spanNorm, desde = 0) {
  const seg = SEGMENTOS_EXAMEN.find((s) => s.id === segId);
  if (!seg) return { hallazgos: [], hayDuda: false };
  const hallazgos = [];
  let hayDuda = false;
  for (const h of seg.hallazgos) {
    const toks = _SINONIMOS_HALLAZGO[h.v] || [_norm(h.v)];
    const lecturas = toks.map((tk) => _lectura(spanNorm, tk, desde));
    if (lecturas.includes('si')) hallazgos.push(h.v);
    else if (lecturas.includes('duda')) hayDuda = true;
  }
  return { hallazgos, hayDuda };
}

// "Normal" con borde de palabra: /\bnormal/ NO matchea 'anormal' (no hay
// frontera antes de la n). Cubre las formas que la enfermera usa de verdad.
// Sin "conservado" (2026-10-27): describe una PARTE ("expansión conservada",
// "RHA conservados") y declaraba normal el segmento entero, frase basal
// incluida ("torax expansion conservada con crepitos" salía "sin ruidos
// agregados"). Y nunca con una excepción dicha ("normal salvo sibilancias").
const _NORMAL_RE = /\bnormal|sin hallazgos|sin alteraciones|sin particularidades|sin lesiones|sin novedad|sin problemas|\bindemne/;
const _EXCEPCION_RE = /\b(salvo|excepto|pero|aunque)\b/;
const _GLOBAL_NORMAL_RE = /resto (sin hallazgos|normal|sin alteraciones)|(lo demas|el resto) (normal|sin hallazgos|sin alteraciones)|examen (fisico )?(normal|sin hallazgos)|todo (normal|sin hallazgos)|sin hallazgos en general|sin alteraciones en general/;

export function parseExamenDictado(texto) {
  const norm = _norm(texto);
  const parsed = { impresion: null, conciencia: null, posicion: null, segmentos: {}, nSegmentos: 0, nHallazgos: 0 };
  if (!norm) return parsed;

  parsed.impresion = _matchPrimero(norm, _IMPRESION_CUES);
  parsed.conciencia = _matchPrimero(norm, _CONCIENCIA_CUES);
  parsed.posicion = _matchPrimero(norm, _POSICION_CUES);

  const tocados = new Set();
  // Segmentos donde el lector calló algo por una negación dudosa. Callar está
  // bien (queda a la vista para marcarlo a mano), pero declararlos normales
  // NO: "extremidades inferiores sin lesiones edema bilateral" (sin comas,
  // como lo deja el reconocedor del navegador) quedaba firmado "sin edema".
  // Quedan sin constatar y se ofrecen en "El resto" con su frase a la vista.
  const dudosos = new Set();
  for (const { id, texto: spanNorm, desde } of _segmentSpans(norm)) {
    if (tocados.has(id)) continue; // la primera aparición del segmento manda
    const { hallazgos, hayDuda } = _matchHallazgos(id, spanNorm, desde);
    const propio = spanNorm.slice(desde);
    if (hallazgos.length) {
      parsed.segmentos[id] = { hallazgos, sinHallazgos: false };
      parsed.nHallazgos += hallazgos.length;
      tocados.add(id);
    } else if (hayDuda) {
      dudosos.add(id);
    } else if (_NORMAL_RE.test(propio) && !_EXCEPCION_RE.test(propio)) {
      parsed.segmentos[id] = { hallazgos: [], sinHallazgos: true };
      tocados.add(id);
    }
  }

  // "resto normal" DICHO (no silencio): marca sin-hallazgos los segmentos del
  // catálogo que no se tocaron. Honra una declaración global explícita; nunca
  // inventa normalidad sobre lo callado (ni sobre un segmento con una duda).
  if (_GLOBAL_NORMAL_RE.test(norm)) {
    for (const seg of SEGMENTOS_EXAMEN) {
      if (!tocados.has(seg.id) && !dudosos.has(seg.id)) {
        parsed.segmentos[seg.id] = { hallazgos: [], sinHallazgos: true };
        tocados.add(seg.id);
      }
    }
  }

  parsed.nSegmentos = tocados.size;
  return parsed;
}

// Funde lo dictado sobre el examen actual (mismo espíritu que mergeEvolucion:
// no pisa lo marcado). La apertura rellena solo si estaba vacía; los hallazgos
// se unen; el "sin hallazgos" dictado solo aplica si el segmento no quedó con
// detalle (detalle manda). Pura: no muta `ex`.
export function mergeExamenDictado(ex, parsed) {
  const base = ex && typeof ex === 'object' ? ex : estadoInicialExamen();
  const p = parsed || { segmentos: {} };
  const next = {
    ...base,
    impresion: base.impresion || p.impresion || null,
    conciencia: base.conciencia || p.conciencia || null,
    posicion: base.posicion || p.posicion || null,
    segmentos: { ...(base.segmentos || {}) },
  };
  for (const [id, seg] of Object.entries(p.segmentos || {})) {
    const prev = segmentoExamenState(base, id);
    const hallazgos = Array.from(new Set([...prev.hallazgos, ...(seg.hallazgos || [])]));
    next.segmentos[id] = {
      ...prev,
      hallazgos,
      sinHallazgos: hallazgos.length ? false : (prev.sinHallazgos || Boolean(seg.sinHallazgos)),
    };
  }
  return next;
}

// ── Benito ve el examen ───────────────────────────────────────────────────────
// Estado del examen de ingreso en altura de resumen_paciente: SOLO hechos de
// catálogo cerrado (hecho/hora/completitud) — nunca los hallazgos ni la nota,
// para no filtrar el estado clínico del paciente por una tool conversacional.
// La hora es la evidencia de "examen temprano"; el conteo, cuánto se recorrió.
export function resumenExamenBenito(ex) {
  if (!examenTieneContenido(ex)) return 'Sin examen físico registrado aún.';
  const comp = completitudExamen(ex);
  const cuando = ex?.hora ? `hecho a las ${ex.hora}` : 'iniciado';
  return `Examen físico: ${cuando} (${comp.conDatos}/${comp.total} segmentos recorridos).`;
}

// ── Continuidad: qué dejó registrado el ingreso anterior ──────────────────────
// Referencia PII-safe para "retomar de tu turno anterior": SOLO hallazgos de
// catálogo (nunca la nota libre) del examen previo, agrupados por segmento. Es
// el respaldo médico-legal de la continuidad — una LPP registrada al ingreso
// anterior sigue siendo "venía de antes" en el turno nuevo. null si el examen
// previo no traía hallazgos de catálogo (una foto normal no necesita eco).
// `lpp: true` destaca la línea de integridad cutánea (D-10): ESE es el dato
// que protege al que recibe — la UI lo realza, sin alarmismo (es respaldo,
// no emergencia). Si el examen previo estructuró la LPP (campos de sacra), la
// categoría/localización acompañan al hallazgo.
const HALLAZGOS_LPP_PREVIA = new Set([
  'LPP presente al recibir', 'Lesión por presión en desarrollo (DTPI)',
  'Eritema que no blanquea', 'LPP o eritema en talones',
]);

export function resumenExamenPrevio(ex) {
  if (!ex) return null;
  const lineas = [];
  for (const seg of SEGMENTOS_EXAMEN) {
    const { hallazgos, campos } = segmentoExamenState(ex, seg.id);
    const validos = seg.hallazgos.map((h) => h.v).filter((v) => hallazgos.includes(v));
    if (!validos.length) continue;
    const linea = { label: seg.label, hallazgos: validos };
    if (seg.id === 'sacra' && validos.some((v) => HALLAZGOS_LPP_PREVIA.has(v))) {
      linea.lpp = true;
      const detalle = camposSegmentoTexto('sacra', campos);
      if (detalle) linea.detalle = detalle.replace(/\.$/, '');
    }
    lineas.push(linea);
  }
  // Piel del mapa corporal (aditivo): las lesiones estructuradas del ingreso
  // anterior acompañan al eco — la LPP previa es el dato que protege al que
  // recibe. Clave `piel` opcional; el shape histórico { hora, lineas } no cambia.
  const piel = resumenPielPrevio(ex);
  if (!lineas.length && !piel) return null;
  const resumen = { hora: ex.hora || null, lineas };
  if (piel) resumen.piel = piel;
  return resumen;
}

// ── Memoria del examen: líneas de ingreso por sistema ──────────────────────────
// Para cada sistema de la evolución, extrae los hallazgos del examen físico que
// se rutean a ese sistema, agrupados por segmento y con la hora de ingreso.
// Devuelve { hallazgos: string[], hora: string | null } o null si no hay hallazgos
// relevantes para ese sistema.
export function examenPorSistema(ex, sistemaId) {
  if (!ex || !sistemaId) return null;
  const hallazgos = [];
  const hora = ex.hora || null;

  for (const seg of SEGMENTOS_EXAMEN) {
    const { hallazgos: segHallazgos } = segmentoExamenState(ex, seg.id);
    for (const hallazgoObj of seg.hallazgos) {
      const h = hallazgoObj.v;
      if (segHallazgos.includes(h) && hallazgoObj.evolucion === sistemaId) {
        hallazgos.push({ segmento: seg.label, hallazgo: h });
      }
    }
  }

  // Piel: las lesiones de ingreso siempre routean a tegumentario
  if (sistemaId === 'tegumentario' && ex.piel && Array.isArray(ex.piel.lesiones)) {
    for (const lesion of ex.piel.lesiones) {
      // La v2 se nombra con su propia cabeza ("desgarro cutáneo tipo 2 (ISTAP)
      // en antebrazo izquierdo"); la histórica conserva su texto de siempre.
      if (lesion?.v === 2) {
        hallazgos.push({ segmento: 'Piel', hallazgo: anclaLesion(lesion) });
        continue;
      }
      const zonaDef = ZONAS_PRESION.find((z) => z.id === lesion.zona);
      const label = zonaDef?.label || `zona ${lesion.zona}`;
      const cat = lesion.categoria ? ` ${lesion.categoria}` : '';
      const desc = `LPP o lesión en ${label}${cat}`;
      hallazgos.push({ segmento: 'Piel', hallazgo: desc });
    }
  }

  return hallazgos.length > 0 ? { hallazgos, hora } : null;
}

// La frase determinista que "Sin cambios" REGISTRA en la evolución del sistema.
// Solo re-afirma lo que el examen constató (catálogo cerrado) + la ausencia de
// cambio — NUNCA declara normalidad de lo que no se examinó (misma regla que
// las basales: el flag no puede firmar lo que no vio). Es la línea que la UI
// previsualiza Y la que aterriza en la nota del sistema al marcar el chip
// (una sola fuente: preview y documento no pueden divergir).
export function lineaSinCambiosExamen(ex, sistemaId) {
  const memoria = examenPorSistema(ex, sistemaId);
  if (!memoria) return null;
  const lista = memoria.hallazgos.map((h) => lowerFirst(h.hallazgo)).join(', ');
  const verbo = memoria.hallazgos.length > 1 ? 'persisten' : 'persiste';
  // Sin hora impresa (misma norma 2026-08-02 que sacó la hora del resto de la
  // prosa): "el de ingreso" ES la referencia; la hora sigue en los datos.
  const ref = ' respecto al de ingreso';
  return `Al examen físico, sin cambios${ref}: ${verbo} ${lista}.`;
}
